// Grids & Gantts — the structure grid.
//
// The rows a structure draws, in the columns the config names — and, when
// it is switched on, the TIMELINE beside them, in the same scroller over
// the same windowed rows. The timeline is a pane of the structure, not a
// chart next to it: a bar cannot drift out of line with its own cells
// because they are the same row, and opening a folder opens it in both at
// the same instant for the same reason.
//
// IT WINDOWS, and that is why the shared Gantt.jsx is not used here. That
// component draws every row it is given, which is right for a few hundred
// and fatal for twenty-eight thousand: only the rows in view are rendered,
// over a spacer the height of the rest. Rows are a FIXED height for
// exactly that reason — a variable height means measuring every row to
// know where the scroll is.
//
// Four row kinds, drawn the way a structure draws them:
//   rule    a generator, printed above what it generates
//   folder  a named box with its own rules inside it
//   group   a field value bracketing what falls under it
//   item    a work item, its cells editable in place

import { cloneElement, isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import { FONT } from './theme';
import TypeIcon from '../shared/TypeIcon';
import { DnaIcon } from '../shared/CloneIcon';
import { RescheduleBadge } from './Rescheduler';
import { CreatePlus, StatusPick } from './RowControls';
import { LineIcon, StockCell } from './StockCell';
import EditField from './EditField';
import { cellOf, fmtNum, numOf, resolvePseudo } from './columns';
import { resolveField } from './fields';
import { isoOf } from './dueIn';
import { GANTT_INTERVALS, ganttGrain } from '../shared/interval';
import { fitColumns, clampSplit, HEAD_MAX, headingFontSize, headingLines, splitFraction, splitPixels, totalWidth, treeColumnWidth } from '../shared/gridFit';
import { MAX_COL_W, isEmptyView, isPinned, mergeUserView, readStore, resetView, sortOf, withAdded, withRemoved, withMoved, withSort, withWidth, withWidths, writeStore } from '../shared/userView';
import { sortKeyOf, sortRows } from '../shared/sortRows';
import { cursorFor, datesAfterDrag, daysFromDx, handleAt, offView, scrollTargetFor, SLOP } from '../shared/barDrag';
import { glideMs, glidePosition, LAND_GRACE, prefersReducedMotion, shouldGlide } from '../shared/glide';
import { addDays, barOf, chartWidth, clipBar, grainFor, PX, superTicks, tailOf, ticksOf, toneOf, todayX, weekendBands, windowOf } from './timeline';
import GanttDependencyOverlay from '@modules/folders/frontend/components/GanttDependencyOverlay';
import { buildDependencyEdges, deriveType, pairAlreadyLinked } from '@modules/folders/frontend/utils/ganttDependencyHelpers';

// Shared so the overlay is not handed a new Map every render.
const EMPTY_PHANTOMS = new Map();
// Where a link dot sits: its centre this far inside each end of the bar,
// which is where GanttDependencyOverlay's arrows enter and leave a bar (its
// own DOT_INSET). On a short bar they move in towards its ends (a quarter
// of its width each side) so a one- or two-day bar has them too, as the
// folders Gantt does; under DOT_MIN_BAR there is no room for two.
const DOT_INSET = 11;
const DOT_MIN_BAR = 14;
const dotInsetFor = (w) => Math.min(DOT_INSET, Math.max(4, Math.round(w / 4)));

// THE BAR'S HEIGHT, in one place. The bar is drawn with it and the arrow
// geometry anchors to it; two literals a thousand lines apart is how an
// arrow comes to point at where a bar used to be.
const barHeightOf = (row) => (row && row.isGroup ? 9 : 16);
import { iconSpec, ROW_H } from './icons';
import PinIcon from '../shared/PinIcon';
import { AddColumn, ColumnMenu, columnFor } from './ColumnMenu';
import DatePopover from '@modules/database/work-items/frontend/editors/DatePopover';

// ---- measuring a summary ------------------------------------------------------
// The tree column is fitted to what it holds (see treeColumnWidth in
// ../shared/gridFit), and "what it holds" is text, so it is measured — with a
// canvas, once, in the font the cell draws in. Not exported: this module
// exports its component and nothing else, or Fast Refresh gives up on it.
let measureCtx = null;
let measureFont = '';
function measureSummary(text, level) {
  if (typeof document === 'undefined') return String(text).length * 7;
  if (!measureCtx) {
    const c = document.createElement('canvas');
    measureCtx = c.getContext('2d');
    if (!measureCtx) return String(text).length * 7;
  }
  const font = `${level <= 1 ? 600 : 400} 13px ${FONT}`;
  if (font !== measureFont) { measureCtx.font = font; measureFont = font; }
  return measureCtx.measureText(String(text)).width;
}


// ---- cells that edit in place ------------------------------------------------
// It saves on blur or Enter and gives the value back on Escape, so a
// mis-click never writes anything.
function EditText({ value, onSave, ui, style, title, startOpen = false, onDone }) {
  const [editing, setEditing] = useState(startOpen);
  if (!editing) {
    return (
      <span role="button" tabIndex={-1} title={title ? `${title} — click to change` : 'Click to change'}
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        style={{ ...style, cursor: 'text', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
        {value || <span style={{ color: ui.faint }}>—</span>}
      </span>
    );
  }
  return (
    <input autoFocus defaultValue={value || ''} onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={(e) => { setEditing(false); if (onDone) onDone(); if (e.target.value !== value) onSave(e.target.value); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { e.currentTarget.value = value || ''; e.currentTarget.blur(); } }}
      style={{ ...style, width: '100%', font: 'inherit', fontFamily: FONT, padding: '1px 4px', border: `1px solid ${ui.primary}`, borderRadius: 4, background: ui.card, color: ui.fg, outline: 'none' }} />
  );
}

// THE WHOLE CELL IS THE TARGET.
//
// Every editor on this grid used to be sized to its own TEXT, so an empty
// date was a two-pixel dash floating in a hundred-pixel cell and the only way
// to set it was to hit that dash. Aiming at an em-dash is not an interaction.
// The rule now, for dates, numbers, text and pickers alike: if the cell can
// be edited, anywhere in the cell starts the edit — which is what the status
// chip has done since the day somebody said it read as broken.
const FILL = {
  width: '100%', height: '100%', display: 'flex', alignItems: 'center',
  minWidth: 0, boxSizing: 'border-box',
  // THE PADDING BELONGS TO THE TARGET. Left on the cell around the editor, a
  // 12px strip down each side of every column looked editable and was not.
  padding: '0 12px',
};
const fill = (align) => ({
  ...FILL,
  justifyContent: align === 'right' ? 'flex-end' : (align === 'center' ? 'center' : 'flex-start'),
});

// AN EDITOR OPENS ON THE VALUE IT IS EDITING — AND THE CALENDAR OPENS WITH IT.
//
// This has been two things and each was half right. First it was a one-pixel
// invisible input told to showPicker(): a calendar appeared, and the date the
// cell already held was nowhere on it. Then it was a real date field opened
// in place with the value in it, and the calendar was a second click away —
// so changing a date became two clicks, which somebody counted.
//
// Now it is both. The field opens in place holding the current date, and the
// browser's calendar is opened ON that date in the same click. Pick a day and
// it is saved and the editor closes; type instead and it saves on Enter or on
// leaving, as before. Where a browser will not open its picker without a
// second gesture, the field is still there with the value in it, one click
// from the calendar — no worse than it was.
//
// A TYPED DATE IS NOT SAVED HALF-TYPED. A date input reports a change on every
// keystroke once its segments are full, so the year "2" is "0002" and the year
// "20" is "0020" — saving on change would write those. So a keystroke marks
// the edit as typed, and a typed edit saves the old way; only a date the
// calendar handed over is saved the moment it arrives.
//
// 2026-10-02: NOT THE BROWSER'S CALENDAR ANY MORE. Chrome's picker writes a
// whole date into the input the moment its month or year selector changes,
// and this cell saved on change — so turning to next month saved a date
// nobody picked. The cell now opens Stride's own calendar (DatePopover) in
// the one click: moving between months writes nothing, a day click saves.
function EditDate({ text, iso, onSave, ui, align, title }) {
  const [rect, setRect] = useState(null);
  const was = iso || '';
  return (
    <span role="button" tabIndex={-1} style={{ ...fill(align), cursor: 'pointer', background: rect ? `${ui.primary}14` : undefined }}
      title={title || (text ? `${text} — click anywhere in this cell to change the date` : 'Empty — click anywhere in this cell to set a date')}
      onClick={(e) => { e.stopPropagation(); setRect(e.currentTarget.getBoundingClientRect()); }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {text || <span style={{ color: ui.faint }}>—</span>}
      </span>
      {rect && <DatePopover anchor={rect} value={was} onClose={() => setRect(null)}
        onPick={(v) => { if ((v || '') !== was) onSave(v || ''); }} />}
    </span>
  );
}

// ESCAPE MUST NOT WRITE. This cleared its own input and then blurred, and
// blur saved — so the way out of a number cell you opened by mistake was the
// way to empty the field. It now marks the escape and the blur honours it.
// `own` is what the EDITOR opens on when it differs from what the cell SHOWS
// — a Σ column on a work item shows the total of its branch and edits that
// item's own hours. Without it, opening the editor on a parent would offer to
// overwrite one item with the sum of forty.
function EditNum({ value, onSave, ui, align, own, title }) {
  const [editing, setEditing] = useState(false);
  const cancelled = useRef(false);
  if (!editing) {
    return (
      <span role="button" tabIndex={-1}
        title={title || (value ? `${value} — click anywhere in this cell to change it` : 'Empty — click anywhere in this cell to set a number')}
        onClick={(e) => { e.stopPropagation(); cancelled.current = false; setEditing(true); }}
        style={{ ...fill(align), cursor: 'text' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || <span style={{ color: ui.faint }}>—</span>}
        </span>
      </span>
    );
  }
  const shown = own === undefined ? value : own;
  const was = shown === '' || shown == null ? '' : String(shown).replace(/,/g, '');
  return (
    <input autoFocus type="number" defaultValue={was} className="wip-num"
      // SELECTED, NOT CLEARED. The value is there to be read; selecting it
      // means one keystroke still replaces it, which is what somebody
      // retyping a number wants, without ever having hidden it from
      // somebody who only wanted to change a digit.
      onFocus={(e) => e.currentTarget.select()}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        setEditing(false);
        // Nothing typed, or escaped out: nothing written. A blur that saves
        // an unchanged value costs a request and a spinner for no change.
        if (cancelled.current) { cancelled.current = false; return; }
        if (e.target.value !== was) onSave(e.target.value);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { cancelled.current = true; e.currentTarget.blur(); } }}
      style={{ width: '100%', textAlign: align, font: 'inherit', fontFamily: FONT, padding: '1px 4px', border: `1px solid ${ui.primary}`, borderRadius: 4, background: ui.card, color: ui.fg, outline: 'none' }} />
  );
}

// ---- the renderer ------------------------------------------------------------
// One place decides what every cell of every row kind looks like. It is
// NOT exported: a .jsx module that exports anything but components cannot
// Fast Refresh, and on this page a full reload costs a three-minute
// rebuild. Constants live in ./icons.js for the same reason.
// A folder's icon: a glyph, or a small picture when the admin gave an address.
function FolderIcon({ icon }) {
  const spec = iconSpec(icon);
  if (spec.src) return <img src={spec.src} alt="" width={16} height={16} style={{ flex: '0 0 auto', objectFit: 'contain' }} />;
  return <span style={{ flex: '0 0 auto' }}>{spec.text}</span>;
}

function makeRowRenderer({
  cols, ctx, ui, S, workTypes, isDark, editable, onToggle, onOpenItem, onEdit, savingKeys,
  api, onCreated, onStatusChanged, renaming, onRename = () => {}, onEditRule, onRemoveRule = null, itemHref, narrow,
  selected = null, onPick = null, cloneable = null, onDraft = null, draft = null,
  // The folder drag lives in the grid's own state and is handed in here —
  // this renderer is a plain function outside the component and sees
  // nothing it is not given.
  onMoveFolder = null, dragFolder = null, dropOn = null, setDropOn = () => {},
  // THE PINNED COLUMNS: { left: { [colId]: px }, last } — or null. A pinned
  // cell is sticky at its offset, painted with its row's own background
  // (--wip-row-bg, set on the row) so what scrolls under it is hidden, and
  // the last one casts an edge once the reader has scrolled sideways.
  frozen = null, scrolledX = false,
}) {
  const paneW = cols.reduce((a, c) => a + c.width, 0);
  // Every cell but the last carries the line between columns — a structure
  // is read across as much as down, and a column with no edge makes the
  // eye guess which number belongs to which heading.
  const sep = (c) => (cols[cols.length - 1] === c ? null : { borderRight: `1px solid ${ui.border}` });
  const cellBase = {
    display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: 13,
    whiteSpace: 'nowrap', overflow: 'hidden', boxSizing: 'border-box', height: '100%',
  };
  // A column of figures only reads as a column when the digits are the same
  // width — otherwise 111 looks shorter than 999 and the eye cannot compare
  // down the column at all.
  const numeric = (c) => (c.align === 'right' || ['number', 'count', 'countSub', 'countIf', 'ratio', 'diff', 'divide'].includes(c.kind)
    ? { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' } : null);
  const frozenStyle = (c) => {
    if (!frozen || frozen.left[c.id] == null) return null;
    return {
      position: 'sticky', left: frozen.left[c.id], zIndex: 2,
      background: `var(--wip-row-bg, ${ui.card})`,
      ...(scrolledX && frozen.last === c.id ? { boxShadow: '6px 0 6px -4px rgba(0,0,0,0.22)' } : null),
    };
  };
  const colStyle = (c, extra) => ({
    ...cellBase,
    flex: `0 0 ${c.width}px`,
    width: c.width,
    justifyContent: c.align === 'right' ? 'flex-end' : (c.align === 'center' ? 'center' : 'flex-start'),
    ...numeric(c),
    ...extra,
    ...frozenStyle(c),
  });
  const toneColor = (tone) => (tone === 'late' ? ui.red : (tone === 'muted' || tone === 'agg' ? ui.muted : ui.fg));

  // Anything that opens a work item is an <a href>, not a <div onClick>.
  //
  // MIDDLE CLICK AND CTRL-CLICK ARE THE BROWSER'S, and the href is what gives
  // them to us: a new browser tab showing THAT WORK ITEM'S OWN SCREEN, full
  // window, and nothing else — not a search results page with one row in it.
  // A plain left click is prevented and handled here, so the reader who just
  // wants a look stays where they are and keeps their place in the structure.
  const openLink = (r, children, style, title) => {
    const href = itemHref && r.it ? itemHref(r.key) : null;
    const go = (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      e.stopPropagation();
      onOpenItem(r.it);
    };
    if (!href) {
      return (
        <span role="button" tabIndex={-1} title={title}
          onClick={(e) => { e.stopPropagation(); onOpenItem(r.it); }}
          style={{ ...style, cursor: 'pointer' }}>{children}</span>
      );
    }
    return (
      <a href={href} title={title} onClick={go}
        style={{ ...style, cursor: 'pointer', textDecoration: 'none', color: style && style.color ? style.color : 'inherit' }}>{children}</a>
    );
  };

  // The tree cell: the twisty, the icon, the name. Every row kind has one;
  // only its contents differ.
  const treeCell = (r, c) => {
    const indent = 4 + r.level * 15;
    // THE TWISTY IS THE MOST-CLICKED THING ON THE PAGE and it was a 10px
    // glyph in a 12px box — a target smaller than the mouse pointer drawn
    // over it. It is now 18px wide and 13px tall with a hover plate behind
    // it, which is a real button without costing the tree cell its text: the
    // indent step stays at 15px, so the structure reads exactly as before.
    const twisty = (
      <button className={r.hasKids ? 'wip-twisty' : undefined}
        onClick={(e) => { e.stopPropagation(); if (r.hasKids) onToggle(r.path); }}
        aria-label={r.hasKids ? (r.open ? 'Collapse' : 'Expand') : undefined}
        title={r.hasKids ? (r.open ? 'Collapse' : 'Expand') : undefined}
        style={{
          background: 'none', border: 'none', padding: 0, width: 18, flex: '0 0 18px', height: 20,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4,
          cursor: r.hasKids ? 'pointer' : 'default', color: r.hasKids ? ui.fg : ui.muted,
          // 17px, a third bigger than the 13 it was: the arrow is the target,
          // and it read small beside the "+" and the icons.
          fontSize: 17, lineHeight: 1, fontFamily: FONT,
        }}>
        {r.hasKids ? (r.open ? '▾' : '▸') : ''}
      </button>
    );
    if (r.kind === 'folder') {
      const fid = r.folder && r.folder.id;
      const dropHere = !!onMoveFolder && dropOn === r.path;
      return (
        <div key={c.id} style={{ ...colStyle(c, sep(c)), paddingLeft: indent, gap: 6, cursor: 'pointer', fontWeight: 700, outline: dropHere ? `2px solid ${ui.amber}` : 'none', outlineOffset: -2 }}
          onClick={() => onToggle(r.path)}
          draggable={!!onMoveFolder}
          title={onMoveFolder ? 'Drag this folder onto another folder to put it inside that one, or onto the rules at the top to put it back at the top' : undefined}
          onDragStart={onMoveFolder ? (e) => { dragFolder.current = fid; e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', fid); } catch { /* some browsers refuse */ } } : undefined}
          onDragEnd={onMoveFolder ? () => { dragFolder.current = null; setDropOn(null); } : undefined}
          onDragOver={onMoveFolder ? (e) => { if (dragFolder.current && dragFolder.current !== fid) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropOn !== r.path) setDropOn(r.path); } } : undefined}
          onDragLeave={onMoveFolder ? () => { if (dropOn === r.path) setDropOn(null); } : undefined}
          onDrop={onMoveFolder ? (e) => { e.preventDefault(); const id = dragFolder.current; dragFolder.current = null; setDropOn(null); if (id && id !== fid) onMoveFolder(id, fid); } : undefined}>
          {twisty}
          <FolderIcon icon={r.icon} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: ui.blue }}>{r.summary}</span>
        </div>
      );
    }
    if (r.kind === 'group') {
      return (
        <div key={c.id} style={{ ...colStyle(c, sep(c)), paddingLeft: indent, gap: 6, cursor: 'pointer', fontWeight: 600 }}
          onClick={() => onToggle(r.path)}>
          {twisty}
          {/* A GROUP LOOKS LIKE WHAT IT GROUPS BY. Grouped by work type, the
              header wears that type's own icon, the one its rows wear; by
              status, it IS the status's lozenge, in its colours — drawn from
              one work item of the group, the same way the cells draw theirs.
              Anything else keeps a project's avatar or the plain mark. */}
          {r.look === 'status' && r.sample ? (
            <span style={{ flex: '0 1 auto', minWidth: 0, display: 'inline-flex' }} title={`Group by ${r.groupField}`}>
              <StatusPick item={r.sample} text={r.summary} ui={ui} S={S} disabled />
            </span>
          ) : (
            <>
              {r.look === 'type' && r.sample
                ? <TypeIcon issue={r.sample} types={workTypes} ui={ui} size={14} />
                : (r.icon
                  ? <img src={r.icon} alt="" width={14} height={14} style={{ flex: '0 0 auto', borderRadius: 3 }} title={`Group by ${r.groupField}`} />
                  : <span style={{ flex: '0 0 auto', color: ui.muted, fontSize: 11 }} title={`Group by ${r.groupField}`}>▦</span>)}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.summary}</span>
            </>
          )}
          {/* THE PERIOD THAT HOLDS TODAY says so ("This Month") — the
              header keeps its real period; the tag is how a reader finds now. */}
          {r.tag ? (
            <span title={`${r.summary} holds today`} style={{ flex: '0 0 auto', fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 7, background: `${ui.blue}1f`, color: ui.blue, border: `1px solid ${ui.blue}55`, whiteSpace: 'nowrap' }}>{r.tag}</span>
          ) : null}
        </div>
      );
    }
    // A TABLE LINE (./lines.js) — a line of the table in its work item's
    // field, drawn under it: no link, no rename, nothing to create under it,
    // its own small icon, and the room the "+" takes on a work item's row so
    // its name starts where its siblings' names do.
    if (r.kind === 'line') {
      const cell = cellOf(c, r, ctx);
      return (
        <div key={c.id} className="wip-tree-cell" style={{ ...colStyle(c, sep(c)), paddingLeft: indent, gap: 5 }}>
          {twisty}
          {api && onCreated ? <span style={{ flex: '0 0 17px', width: 17 }} /> : null}
          <span style={{ color: ui.muted, display: 'inline-flex' }}><LineIcon size={14} /></span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: 0 }}>{cell.text}</span>
        </div>
      );
    }
    // A work item's tree cell. CLICKING THE SUMMARY OPENS THE WORK ITEM —
    // that is what a reader expects of the name of a thing, and it is the
    // one click that gets them the whole screen. Renaming in place is still
    // here, behind the pencil, so the two cannot be confused for each
    // other: an accidental click can no longer overwrite a summary.
    const cell = cellOf(c, r, ctx);
    return (
      <div key={c.id} className="wip-tree-cell" style={{ ...colStyle(c, sep(c)), paddingLeft: indent, gap: 5 }}>
        {twisty}
        {/* CREATE SITS LEFT OF THE NAME, where the eye already is — but it
            only appears on hover, so a screenful of rows is not a screenful
            of buttons. It keeps its WIDTH while hidden, so nothing shifts
            sideways when the mouse arrives. */}
        {api && onCreated ? (
          <span className="wip-plus-wrap" style={{ flex: '0 0 auto', display: 'inline-flex', opacity: 0, transition: 'opacity .1s' }}>
            <CreatePlus item={r.it} ui={ui} S={S} api={api} onCreated={onCreated} narrow={narrow}
              onDraft={onDraft ? (parent, option, options) => onDraft(parent, option, options, r.path) : null} />
          </span>
        ) : null}
        <TypeIcon issue={r.it} types={workTypes} ui={ui} size={14} />
        {renaming === r.path && editable
          ? (
            <EditText startOpen value={cell.text} title={r.key} ui={ui}
              onDone={() => onRename(null)}
              onSave={(v) => { onRename(null); if (v !== cell.text) onEdit(r.it, { summary: v }); }}
              style={{ fontWeight: r.level <= 1 ? 600 : 400, flex: '1 1 auto', minWidth: 0 }} />
          )
          : (
            openLink(r, cell.text, {
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontWeight: r.level <= 1 ? 600 : 400, flex: '1 1 auto', minWidth: 0, display: 'block',
            }, `${r.key} — click to open it here, middle-click for its own tab`)
          )}
        {/* NO "· N" AFTER THE NAME. The count of what is under a shut row was
            asked for on the summary and then asked to go: it read as a value
            in the tree column, and the Count Sub-Items column already says it. */}
        {/* THE DNA STRAND, right of the name, on a row of a type the admin
            opened up for cloning. It appears on hover, a little larger than
            the text so it is not mistaken for a glyph; clicking it picks the
            row for cloning, and then it STAYS — so the rows going to the
            cloner can be told from a plain selection at a glance. */}
        {cloneable && onPick && r.it && cloneable(r.it) ? (() => {
          const picked = !!(selected && selected.has(r.key));
          return (
            <span className={picked ? undefined : 'wip-row-tools'} style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', opacity: picked ? 1 : 0, marginLeft: 2 }}>
              <button onClick={(e) => { e.stopPropagation(); onPick(r, { add: !picked }); }}
                title={picked ? `${r.key} is picked for cloning — click to unpick it` : `Clone ${r.key} — pick it, then press Clone`}
                style={{ background: picked ? `${ui.blue}1f` : 'none', border: 'none', borderRadius: 6, padding: '0 2px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
                <DnaIcon size={28} title={picked ? `${r.key} is picked for cloning` : `Clone ${r.key}`} />
              </button>
            </span>
          );
        })() : null}
        {editable ? (
          <span className="wip-row-tools" style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', opacity: 0 }}>
            <button onClick={(e) => { e.stopPropagation(); onRename(r.path); }} title="Rename here"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: ui.faint, fontSize: 11.5, lineHeight: 1, fontFamily: FONT }}>✎</button>
          </span>
        ) : null}
      </div>
    );
  };

  const dataCell = (r, c) => {
    const cell = cellOf(c, r, ctx);
    // AN EMPTY CELL IS STILL AN EDITABLE CELL. Returning a blank div for
    // anything with no text was a one-way door: clear an Assigned Resource
    // and there was no longer anything to click to put one back. It hit
    // every editable kind — a date nobody had set could never be set, and a
    // number the same. Only a cell that cannot be written at all gets the
    // blank. A READ-ONLY column (c.readOnly) is never written from the grid.
    const writable = editable && api && r.it && !c.readOnly && ['date', 'number', 'text'].includes(c.kind);
    if (!cell.text && !cell.link && !writable) return <div key={c.id} style={colStyle(c, sep(c))} />;
    // A STOCK CHECK cell: its chip, and a hover card with the sum behind it.
    if (c.kind === 'stock') return <StockCell key={c.id} cell={cell} col={c} row={r} ctx={ctx} ui={ui} style={colStyle(c, sep(c))} />;
    if (c.kind === 'key') {
      // A CHECKBOX LEFT OF THE KEY, on hover — and on every row once one is
      // ticked, so the second is not a hunt. Shift-click ticks the run
      // between the last tick and this one; Shift+↓ / Shift+↑ do the same a
      // row at a time from the keyboard.
      const picked = !!(selected && r.it && selected.has(r.key));
      return (
        <div key={c.id} style={colStyle(c, { ...sep(c), gap: 5, paddingLeft: onPick ? 6 : 12 })}>
          {onPick && r.it ? (
            <input type="checkbox" className="wip-pick" checked={picked} readOnly
              aria-label={`Select ${r.key}`} title={picked ? `${r.key} is selected` : `Select ${r.key} — Shift-click for a run`}
              onClick={(e) => { e.stopPropagation(); onPick(r, { shift: e.shiftKey }); }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ margin: 0, flex: '0 0 auto', cursor: 'pointer', opacity: picked ? 1 : undefined, accentColor: ui.primary }} />
          ) : null}
          {openLink(r, cell.text,
            { color: ui.blue, fontSize: 12, fontFamily: FONT, fontWeight: r.level <= 1 ? 700 : 400 },
            `${r.key} — click to open it here, middle-click for its own tab`)}
        </div>
      );
    }
    if (c.kind === 'status') {
      // The WHOLE CELL is the target, not the chip inside it. A status chip
      // is about eighty pixels of text in a cell twice that wide, and
      // needing to hit the text exactly is why this read as broken.
      return (
        <div key={c.id} style={colStyle(c, { ...sep(c), padding: 0 })}>
          <StatusPick item={r.it} text={cell.text} tone={cell.tone} ui={ui} S={S} api={api} narrow={narrow}
            disabled={!editable || !api || !r.it} onOpenItem={onOpenItem} onChanged={onStatusChanged || (() => {})} />
        </div>
      );
    }
    if (c.kind === 'date' && writable) {
      return (
        <div key={c.id} style={colStyle(c, { ...sep(c), padding: 0, color: toneColor(cell.tone), fontWeight: cell.tone === 'late' ? 700 : 400 })}>
          <EditDate text={cell.text} iso={isoOf(cell.raw)} isDark={isDark} ui={ui} align={c.align}
            onSave={(v) => onEdit(r.it, { [cell.fieldId]: v || null })} />
        </div>
      );
    }
    // A Σ COLUMN EDITS TOO, and what it edits is this row's OWN value.
    //
    // It used to refuse, on the reasoning that a total is not a thing you can
    // type into — which is true of a folder, and false of the work item where
    // the hours actually live. On a leaf the total IS its own value and the
    // distinction never comes up; on a parent the cell keeps showing the
    // total, the editor opens on the item's own hours, and the total
    // re-adds itself the moment it is saved. The tooltip says so, because a
    // number that changes to something other than what you typed needs to
    // have warned you first.
    //
    // A FORMATTED FIGURE EDITS AS ITS NUMBER. The cell may read "R 1 789.09";
    // the editor always opens on the item's raw value (1789.09). Opening it on
    // the printed text handed a number input a string it cannot hold — it
    // showed empty, and the blur that followed wrote the field away.
    if (c.kind === 'number' && writable) {
      const ownNum = numOf(r.it, cell.fieldId);
      const isSum = c.agg === 'sum';
      const differs = isSum && cell.num != null && ownNum !== cell.num;
      return (
        <div key={c.id} style={colStyle(c, { ...sep(c), padding: 0 })}>
          <EditNum value={cell.text} align={c.align} ui={ui}
            own={ownNum == null ? '' : ownNum}
            title={differs
              ? `${cell.text} is the total below this row. Click anywhere in this cell to change ${r.key}'s own value (${fmtNum(ownNum) || 'none'}).`
              : (cell.text ? `${cell.text} — click anywhere in this cell to change it` : 'Empty — click anywhere in this cell to set a number')}
            onSave={(v) => onEdit(r.it, { [cell.fieldId]: v === '' ? null : Number(v) })} />
        </div>
      );
    }
    if (c.kind === 'progress' && cell.pct != null && !cell.band) {
      return (
        <div key={c.id} style={colStyle(c, { ...sep(c), gap: 6 })}>
          <span style={{ flex: '1 1 auto', height: 5, borderRadius: 5, background: ui.surface, overflow: 'hidden', minWidth: 24 }}>
            <span style={{ display: 'block', width: `${cell.pct}%`, height: '100%', background: ui.green }} />
          </span>
          <span style={{ ...S.tiny, flex: '0 0 auto' }}>{cell.text}</span>
        </div>
      );
    }
    // A COLOURED BAND (the column's `bands`): a soft chip, or coloured
    // figures. The colour is a theme token, so light and dark both read.
    if (cell.band && cell.text) {
      const bc = ui[cell.band] || ui.fg;
      return (
        <div key={c.id} title={cell.text} style={colStyle(c, { ...sep(c), color: c.bandAs === 'text' ? bc : undefined, fontWeight: c.bandAs === 'text' ? 700 : 400 })}>
          {c.bandAs === 'text' ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell.text}</span> : (
            <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11.5, fontWeight: 700, padding: '1px 7px', borderRadius: 7, background: `${bc}1f`, color: bc, border: `1px solid ${bc}44`, whiteSpace: 'nowrap' }}>{cell.text}</span>
          )}
        </div>
      );
    }
    // EVERY OTHER FIELD EDITS TOO. A select, a multi-select, a user or a
    // plain string — EditField asks the work item what the field accepts
    // and offers exactly that, so nothing is written in a shape the field
    // will refuse.
    if (c.kind === 'text' && writable) {
      return (
        <div key={c.id} style={colStyle(c, { ...sep(c), padding: 0 })}>
          <EditField item={r.it} fieldId={ctx.fieldIds[c.id]} text={cell.text} align={c.align}
            ui={ui} S={S} api={api} narrow={narrow}
            // TWO SHAPES, and the second one is why a saved cell used to go
            // to a dash: the page must believe the DISPLAY shape, not the
            // thin one the server was sent. See ./editShape.
            onSave={(fields, display) => onEdit(r.it, fields, display)} />
        </div>
      );
    }
    return (
      <div key={c.id} title={cell.text}
        style={colStyle(c, { ...sep(c), color: toneColor(cell.tone), fontWeight: cell.tone === 'agg' || (r.isGroup && cell.text) ? 700 : 400 })}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell.text}</span>
      </div>
    );
  };

  // A generator row: the rule, printed the way a structure prints it.
  const ruleRow = (r) => (
    <div className={onEditRule && r.rule.edit ? 'wip-rule' : undefined}
      onClick={onEditRule && r.rule.edit ? (e) => { e.stopPropagation(); onEditRule(r.rule.edit); } : undefined}
      title={onEditRule && r.rule.edit ? 'Click to change this rule' : undefined}
      /* The structure's own rules at the top are where a dragged folder is
         dropped to put it back at the top. */
      onDragOver={onMoveFolder && String(r.path).startsWith('rule/top/') ? (e) => { if (dragFolder.current) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dropOn !== 'top') setDropOn('top'); } } : undefined}
      onDragLeave={onMoveFolder && String(r.path).startsWith('rule/top/') ? () => { if (dropOn === 'top') setDropOn(null); } : undefined}
      onDrop={onMoveFolder && String(r.path).startsWith('rule/top/') ? (e) => { e.preventDefault(); const id = dragFolder.current; dragFolder.current = null; setDropOn(null); if (id) onMoveFolder(id, null); } : undefined}
      // CLIP, NOT HIDDEN, when columns are pinned: overflow:hidden makes the
      // row a scroll container of its own, and the rule's sticky text would
      // then stick to a box that never scrolls.
      style={{ outline: onMoveFolder && dropOn === 'top' && String(r.path).startsWith('rule/top/') ? `2px solid ${ui.amber}` : 'none', outlineOffset: -2, display: 'flex', width: paneW, height: '100%', alignItems: 'center', paddingLeft: 10 + r.level * 15, color: ui.amber, fontSize: 11.5, overflow: frozen ? 'clip' : 'hidden', cursor: onEditRule && r.rule.edit ? 'pointer' : 'default' }}>
      {/* The rule's words travel with the pinned columns, so a reader
          scrolled sideways still sees what generated the rows below. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0, ...(frozen ? { position: 'sticky', left: 10 + r.level * 15 } : null) }}>
      <span style={{ flex: '0 0 auto', opacity: 0.9 }}>{r.rule.icon}</span>
      <span style={{
        flex: '0 0 auto', fontWeight: 700,
        // An "add" line is an invitation, so it is dashed and quieter than
        // the rules it sits under — it is not describing the structure, it
        // is offering to extend it.
        ...(r.rule.add ? { borderBottom: `1px dashed ${ui.amber}`, opacity: 0.95 } : null),
      }}>{r.rule.label}</span>
      {r.rule.value ? <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, opacity: 0.95 }} title={r.rule.value}>{r.rule.value}</span> : null}
      {onEditRule && r.rule.edit ? <span className="wip-rule-pen" style={{ flex: '0 0 auto', marginLeft: 6, opacity: 0, fontSize: 10.5 }}>✎ edit</span> : null}
      {/* AND A BIN BESIDE IT, on every rule that can go — one click, where
          the eye already is, rather than open the editor to find Remove. */}
      {onRemoveRule && r.rule.edit && r.rule.remove ? (
        <button type="button" className="wip-rule-pen"
          title={r.rule.edit.what === 'folder' ? 'Remove this folder (asks first)' : 'Remove this rule — nothing is gone until you Save'}
          aria-label={r.rule.edit.what === 'folder' ? 'Remove this folder' : 'Remove this rule'}
          onClick={(e) => { e.stopPropagation(); onRemoveRule(r.rule.edit); }}
          style={{ flex: '0 0 auto', marginLeft: 2, opacity: 0, background: 'none', border: 'none', padding: '1px 4px', borderRadius: 4, cursor: 'pointer', color: ui.red, display: 'inline-flex', alignItems: 'center' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" />
          </svg>
        </button>
      ) : null}
      </span>
    </div>
  );

  // THE NEW WORK ITEM IS TYPED WHERE IT WILL APPEAR. A create with nothing
  // to it but a summary does not need a dialog: the row it is about to
  // become is drawn in the tree, under its parent, with the type's icon and
  // a line to type on. Enter makes it, Escape forgets it, and the row it
  // becomes takes the same place.
  const draftRow = (r) => {
    const c = cols.find((x) => x.kind === 'summary') || cols[0];
    const indent = 4 + r.level * 15;
    return (
      <div style={{ display: 'flex', width: paneW, height: '100%', background: `${ui.blue}0c` }}>
        {cols.map((col) => (col === c ? (
          <div key={col.id} style={{ ...colStyle(col, sep(col)), paddingLeft: indent + 18, gap: 6 }}>
            <TypeIcon type={draft.type} types={workTypes} ui={ui} size={14} />
            {draft.busy ? (
              <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>{draft.summary}</span>
            ) : (
            <input autoFocus placeholder={`Summary — will be created under ${draft.parentKey}`}
              disabled={!!draft.busy} defaultValue=""
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && e.currentTarget.value.trim()) draft.onSubmit(e.currentTarget.value.trim());
                if (e.key === 'Escape') draft.onCancel();
              }}
              onBlur={(e) => { if (!e.currentTarget.value.trim() && !draft.busy) draft.onCancel(); }}
              style={{ flex: '1 1 auto', minWidth: 0, font: 'inherit', fontFamily: FONT, fontSize: 13, padding: '2px 6px', border: `1px solid ${ui.primary}`, borderRadius: 4, background: ui.card, color: ui.fg, outline: 'none' }} />
            )}
            {draft.busy ? <span style={{ ...S.tiny, flex: '0 0 auto' }}>creating…</span> : null}
            {draft.error ? <span title={draft.error} style={{ color: ui.red, fontSize: 11, flex: '0 0 auto', cursor: 'help' }}>⚠ {draft.error}</span> : null}
          </div>
        ) : <div key={col.id} style={colStyle(col, sep(col))} />))}
      </div>
    );
  };

  const renderRow = (r) => {
    if (r.kind === 'rule') return ruleRow(r);
    if (r.kind === 'draft') return draftRow(r);
    const busy = r.it && savingKeys && savingKeys.has(r.key);
    const bg = r.kind === 'folder' ? `${ui.surface}` : (r.kind === 'group' ? `${ui.surface}88` : 'transparent');
    return (
      <div style={{ display: 'flex', width: paneW, height: '100%', background: bg, opacity: busy ? 0.55 : 1 }}>
        {cols.map((c) => {
          // Each cell says which column it is (data-col), so a click can
          // say which CELL it landed in — see `focusAt` in the grid.
          const el = c.kind === 'summary' ? treeCell(r, c) : dataCell(r, c);
          return isValidElement(el) ? cloneElement(el, { 'data-col': c.id }) : el;
        })}
      </div>
    );
  };

  return { paneW, renderRow, colStyle };
}

// ---- the standalone grid -----------------------------------------------------
export default function Grid({
  rows, cols, ctx, ui, S, workTypes, isDark, editable, height = 560, rowHeight = ROW_H, fitRows = false,
  onToggle, onOpenItem, onEdit, savingKeys, emptyText = 'Nothing in the current filters.',
  // A REAL HREF PER WORK ITEM, so the browser's own affordances work: middle
  // click and Ctrl/Cmd-click open it in a new browser tab, right-click offers
  // Copy link, hover shows where it goes. Left click is intercepted and stays
  // in the page. Without an href a middle click did nothing at all.
  itemHref = null,
  api, onCreated, onStatusChanged,
  // The timeline is not a chart beside the structure — it is a pane of it,
  // over the same windowed rows. Off, the grid is exactly what it was.
  timeline = false, grain, onGrain, storeKey = 'wip',
  // The grid's OWN dependency rules (config.gantt.dependencies). A prop in its
  // own right: `S` beside it is the STYLE object, and reading the rules off
  // that quietly produced undefined — no rules, no edges, no arrows, however
  // the admin had filled the panel in.
  dependencies = null,
  // A DEPENDENCY DRAWN BY HAND: a dot dragged from one bar's end to another
  // bar's end. The grid does the gesture and names the kind ({ type,
  // predKey, succKey }); the page writes the link and whatever follows from
  // it. Left out, there are no dots and nothing can be drawn.
  onLink = null,
  // The bin beside an amber rule line: removes that rule ({ what, … } — the
  // line's own edit descriptor). Admin mode only; left out, no bins.
  onRemoveRule = null,
  // A bar is a HANDLE: dragging it changes the dates it draws. The grid knows
  // pixels and days; only the page knows which FIELDS hold a start and a due,
  // so it is handed { start, due } and writes whatever that means here.
  onTimelineEdit = null,
  // A folder's or a group's span over everything beneath it, asked for ONLY
  // when its bar is about to be drawn. Computing it for all twenty-eight
  // thousand rows up front, on every filter and every edit, was half of why
  // the timeline felt slow — and twenty-nine thousand of those answers were
  // then thrown away unread.
  spanOfRow = null,
  // Columns are changed ON the column, by an admin. Without these the
  // heading row is exactly what a reader sees: a heading row.
  adminMode = false, allColumns = null, fieldList = null, onColumns, onEditRule,
  // ON A PHONE THIS GRID IS A DIFFERENT SHAPE, not a smaller one. See the
  // note above `phoneCols`.
  narrow = false,
  // BACK WHERE THE READER WAS: the page reports where the reader is
  // (onScrolled) and, once per load, hands back a position to start from
  // ({ top, token } — the token changes per load, so a second render of
  // the same load does not yank the reader back).
  restoreTop = null, onScrolled = null,
  // FOLDERS NEST, and an admin nests them by DRAGGING a folder's row onto
  // the folder it should sit in — or onto the structure's own rules at the
  // top, to put it back at the top. onMoveFolder(folderId, parentId|null).
  onMoveFolder = null,
  // THE CHART'S WINDOW, when the page knows better than the rows. The rows
  // on screen are a slice of the structure; a folder's bar is the span of
  // everything beneath it, shut rows included. A page that has every work
  // item in hand can hand over a window that holds them all, so the chart
  // does not rescale under the reader every time a folder opens. Left out,
  // the window is read off the rows, as before.
  dateWindow = null,
  // A READER'S OWN COLUMN NAMES A FIELD THE PAGE MAY NOT HAVE LOADED. The
  // grid resolves the field; this tells the page which ids the columns on
  // screen now want, so it can fetch the ones it has not got.
  onFieldsWanted = null,
  // WHAT PART OF THE PLAN IS ON SCREEN, and at which grain — told to the
  // page whenever it changes, so an export can print the slice the reader
  // is looking at, at the interval they chose, rather than a different
  // chart of its own.
  onChartView = null,
  // SELECTING ROWS. `selected` is the set of work item KEYS ticked (an item
  // under two parents is one item); `onSelect(next, anchorRowId)` is told
  // the new set. `cloneable(item)` says which rows carry the sheep.
  selected = null, onSelect = null, cloneable = null,
  // CREATING IN PLACE. `onDraft(parent, option, options, parentPath)` is
  // told when a "+" has picked a type; `draft` is the row being typed:
  // { parentPath, parentKey, type, busy, error, onSubmit, onCancel }.
  onDraft = null, draft = null,
  // THE PINNED COLUMNS stay put while the others scroll sideways under them.
  // The page's list of column ids (Page settings); left out, the Key and
  // Summary columns. `onFreeze(id, on)` lets an admin unpin from a heading.
  frozenIds = null, onFreeze = null,
  // HOW A HEADING FITS (Page settings → Column headings): 'fit' squeezes it
  // into two or three lines of smaller type; 'full' wraps it whole at full
  // size and the heading row grows to the tallest heading.
  headWrap = 'fit',
  // THE RESCHEDULER (./reschedule.js). `reschedulable(item)` answers the
  // admin's rule for the work item's type (or null) — those bars carry the
  // icon and, dragged whole, the work under them. `rescheduleInfo(key)` is
  // what the page has read about that bar ({ plan, error }), and
  // `onRescheduleIntent(row)` asks it to read it (hover). Left out, bars
  // are exactly what they were.
  reschedulable = null, rescheduleInfo = null, onRescheduleIntent = null,
}) {
  const [renaming, setRenaming] = useState(null);
  const [menuCol, setMenuCol] = useState(null);      // column id whose menu is open
  const headRefs = useRef({});
  const [drag, setDrag] = useState(null);            // { id, startX, startW, scale }

  // DRAGGING A HEADING'S EDGE IS EVERY READER'S, and it changes THEIR view.
  //
  // It used to be admin-only and to write into the shared config, so the one
  // person who could make a column wide enough to read made it wide enough
  // for everybody — and nobody else could do it at all. A width is not a
  // decision about the page, it is a decision about a screen: the admin's
  // width is where everyone starts, and any reader may move their own.
  //
  // THE DELTA IS IN CONFIG PIXELS, NOT SCREEN PIXELS. The columns are fitted
  // to the width available (see ../shared/gridFit), so a set stretched by
  // 1.3 moves 1.3 screen pixels for every config pixel — writing the raw
  // travel back made the heading run away from the mouse.
  useEffect(() => {
    if (!drag) return undefined;
    // THE EDGE UNDER THE MOUSE GOES WHERE THE MOUSE GOES — the column dragged
    // changes and nothing else, the way a structure resizes. With the chart
    // open the columns end at the divider and the Summary takes up whatever
    // they leave (see fitColumns' `fill`), so widening one column would only
    // have squeezed the Summary and the edge would have stood still under the
    // pointer. So the divider moves by the same amount as the column: the
    // Summary keeps its width, and the chart gives or takes the difference.
    const move = (e) => {
      const dx = (e.clientX - drag.startX) / (drag.scale || 1);
      const w = Math.max(40, Math.min(MAX_COL_W, Math.round(drag.startW + dx)));
      setView((v) => withWidth(v, drag.id, w));
      if (drag.split != null && boxW > 0) {
        const px = clampSplit(drag.split + (w - drag.startW), { cols: shownCols, boxW });
        const f = splitFraction(px, boxW);
        splitRef.current = f;
        setSplitFrac(f);
      }
    };
    const up = () => {
      setDrag(null);
      setView((v) => writeStore(storeKey, v));
      if (drag.split != null) rememberSplit(splitRef.current);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  // boxW, shownCols and the split helpers are declared further down: they are
  // READ by the handlers (after this render has finished), never named in
  // this list — naming them here would read a const before it exists.
  }, [drag, storeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- the reader's own view --------------------------------------------------
  // The config says what the page IS; this says what THIS reader wants to see
  // of it — see ../shared/userView. Columns they added, columns they have put
  // away, and what each one is worth to them.
  const [view, setView] = useState(() => readStore(storeKey));
  useEffect(() => { setView(readStore(storeKey)); }, [storeKey]);
  const changeView = (next) => { setView(writeStore(storeKey, next)); };
  // The structural pseudo-fields are this page's own, not the site's — a
  // reader may add "Project" whether or not the field list names it.
  const merged = useMemo(() => mergeUserView(cols, view, { fieldList, alsoKnown: (name) => !!resolvePseudo(name) }), [cols, view, fieldList]);

  // A DENSE GRID DOES NOT RESTRUCTURE ON A PHONE.
  //
  // Dropping to two columns was the obvious move and it is the wrong one:
  // every other Stride page keeps its whole table at every width and
  // lets it scroll inside its own box, with the first column stuck to the
  // left edge so a row can always be named. A reader who wants fewer columns
  // already has a better answer than a breakpoint guessing for them — they
  // hide the ones they do not want, from the same menu they use at a desk,
  // and it is remembered. What a phone changes is the SIZE of the targets and
  // where a menu opens, not what the page contains.
  const shownCols = merged;

  // A READER'S OWN COLUMN READS A REAL FIELD.
  //
  // The page resolves a field id for every column in its CONFIG, by column
  // id — and a column a reader added is not in the config, so its id had no
  // entry, `cellOf` asked the work item for the field '' and every cell came
  // out blank. "I added Project and it stays the same" was exactly this: the
  // column was there, its heading was there, and nothing was ever going to
  // be in it. The reader's columns are resolved here, against the same field
  // list, and the ids they want are reported upward so the page can load the
  // ones it has not fetched.
  const ctxAll = useMemo(() => {
    const mine = shownCols.filter((c) => c.mine);
    // A Difference column reads two others by id — the page's (even hidden)
    // and the reader's own.
    const columns = [...((ctx && ctx.columns) || []), ...shownCols.filter((c) => c.mine)];
    if (!mine.length) return ctx;
    const fieldIds = { ...((ctx && ctx.fieldIds) || {}) };
    mine.forEach((c) => {
      fieldIds[c.id] = c.field === 'duedate' ? 'duedate' : (resolvePseudo(c.field) || resolveField(c.field, fieldList || []) || '');
    });
    return { ...ctx, fieldIds, columns };
  }, [ctx, shownCols, fieldList]);
  const wantedIds = useMemo(() => shownCols.filter((c) => c.mine).map((c) => ctxAll.fieldIds[c.id]).filter(Boolean).sort().join(','), [shownCols, ctxAll]);
  useEffect(() => {
    if (onFieldsWanted && wantedIds) onFieldsWanted(wantedIds.split(','));
  }, [wantedIds, onFieldsWanted]);

  const canEditColumns = adminMode && typeof onColumns === 'function';
  // The columns an admin has pinned: no reader may put one away.
  const pinnedIds = useMemo(() => (cols || []).filter(isPinned).map((c) => c.id), [cols]);
  const everyColumn = allColumns || cols;
  const hiddenColumns = everyColumn.filter((c) => c.on === false);
  const changeCol = (id, patch) => onColumns((list) => list.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeCol = (id) => { onColumns((list) => list.filter((c) => c.id !== id)); setMenuCol(null); };
  const moveCol = (id, d) => onColumns((list) => {
    const i = list.findIndex((c) => c.id === id);
    const j = i + d;
    if (i < 0 || j < 0 || j >= list.length) return list;
    const out = list.slice();
    [out[i], out[j]] = [out[j], out[i]];
    return out;
  });
  // A READER'S OWN SORT. Clicking a heading walks it through ascending,
  // descending and back to the structure's own order. It reorders SIBLINGS —
  // inside each folder, under each parent — because a sort that flattened the
  // tree would throw away the thing the page is for. See ../shared/sortRows.
  const sorted = useMemo(() => {
    if (!view.sort) return rows;
    const col = shownCols.find((c) => c.id === view.sort.id);
    if (!col) return rows;
    return sortRows(rows, (r) => sortKeyOf(cellOf(col, r, ctxAll)), view.sort.dir);
  }, [rows, view.sort, shownCols, ctxAll]);

  // MOVING A COLUMN IS EVERY READER'S, like its width: a heading is dragged
  // onto another, and lands before or after it by which half it is dropped
  // on. The page's columns and the reader's own move alike; the order is
  // THEIR view (../shared/userView), so nobody else's screen moves.
  const dragCol = useRef(null);
  const [colDrop, setColDrop] = useState(null);      // { id, side: 'before'|'after' }

  const scroller = useRef(null);
  // The folder being dragged, and the row it is over — for the outline.
  const dragFolder = useRef(null);
  const [dropOn, setDropOn] = useState(null);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);
  // The header sits inside the scroller, so the rows begin HEAD_H below
  // where scrollTop is measured from. Headings wrapped in full make the row
  // as tall as the tallest one, so its height is MEASURED, not assumed.
  const wrapFull = headWrap === 'full';
  const headRowRef = useRef(null);
  const [headH, setHeadH] = useState(38);
  useEffect(() => {
    const el = headRowRef.current;
    if (!wrapFull || !el || typeof ResizeObserver === 'undefined') { setHeadH(38); return undefined; }
    const measure = () => setHeadH(Math.max(38, Math.round(el.offsetHeight - 2)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [wrapFull]);
  const HEAD_H = wrapFull ? headH : 38;
  // The scroller's visible HEIGHT — named apart from the reader's view of
  // the columns, which is a different thing entirely.
  // FIT TO THE ROWS (fitRows): a page that draws the grid inside its own content lets it be only
  // as tall as the rows that are open — the heading, every row, the scrollbar — never taller
  // than `height`: a folded plan is a short grid, an opened one runs to `height` and scrolls.
  const boxH = fitRows ? Math.max(120, Math.min(height, HEAD_H + rows.length * rowHeight + 20)) : height;
  const [viewH, setViewH] = useState(boxH);
  useEffect(() => { setViewH(boxH); }, [boxH]);
  // Scroll → state, coalesced onto the next animation frame. Both positions
  // are read from the element at that moment, so nothing is stale and nothing
  // is queued twice.
  //
  // THE VERTICAL POSITION IS KEPT IN WHOLE BLOCKS OF ROWS, not in pixels. Kept
  // to the pixel, every frame of a scroll re-rendered the whole grid — the
  // rows, the columns, the chart — sixty times a second, and the browser
  // could not both do that and scroll smoothly: that was the jitter with
  // everything expanded. Rounded down to a block, the state only CHANGES
  // when a block boundary is crossed (React skips a set to the same value),
  // so in between the browser just scrolls what is already drawn — and the
  // window below draws a block's worth of rows beyond the view to scroll
  // into. The horizontal position stays exact: the jump-to-bar button and
  // the chart's visible range are read from it.
  const ROW_BLOCK = 12;
  const blockPx = Math.max(1, ROW_BLOCK * rowHeight);
  const toBlock = (y) => Math.floor(Math.max(0, y) / blockPx) * blockPx;
  // A SET TO THE SAME VALUE IS NOT FREE: React can still run the component
  // once before it notices nothing changed, which was a second whole render
  // at every block boundary. So the last value asked for is remembered, and
  // an unchanged one is not asked for again. Every set goes through these.
  const askedTop = useRef(0);
  const askedLeft = useRef(0);
  const putTop = (y) => { if (y !== askedTop.current) { askedTop.current = y; setTop(y); } };
  const putLeft = (x) => { if (x !== askedLeft.current) { askedLeft.current = x; setLeft(x); } };
  const scrollTick = useRef(0);
  useEffect(() => () => { if (scrollTick.current) cancelAnimationFrame(scrollTick.current); }, []);
  const onScroll = () => {
    if (scrollTick.current) return;
    scrollTick.current = requestAnimationFrame(() => {
      scrollTick.current = 0;
      const el = scroller.current;
      if (!el) return;
      putTop(toBlock(el.scrollTop));
      putLeft(el.scrollLeft);
      if (onScrolled) onScrolled(el.scrollTop);
    });
  };
  const restoredToken = useRef(null);
  useEffect(() => {
    const el = scroller.current;
    if (!restoreTop || !el || !rows.length || restoredToken.current === restoreTop.token) return;
    restoredToken.current = restoreTop.token;
    el.scrollTop = restoreTop.top;
    putTop(toBlock(el.scrollTop));
  }, [restoreTop, rows.length]);
  // Back to the top when the structure underneath changes shape entirely.
  useEffect(() => { if (scroller.current && rows.length === 0) scroller.current.scrollTop = 0; }, [rows.length]);
  // Everything below draws `shown` — the reader's order, which is the
  // structure's own unless they have asked for another.
  // The draft row, if there is one, goes where the new work item will go:
  // after the last row of its parent's subtree, one level in.
  const shown = useMemo(() => {
    const base = view.sort ? sorted : rows;
    if (!draft) return base;
    const i = base.findIndex((r) => r.path === draft.parentPath);
    if (i < 0) return base;
    const level = base[i].level + 1;
    let j = i + 1;
    while (j < base.length && base[j].level >= level) j += 1;
    const row = { kind: 'draft', rowId: '__draft__', path: '__draft__', level, key: '', it: null };
    return [...base.slice(0, j), row, ...base.slice(j)];
  }, [view.sort, sorted, rows, draft]);

  // ---- the width there actually is -------------------------------------------
  // MEASURED, NOT ASSUMED. The columns are fitted to it and the divider is
  // clamped by it, and a guess at either leaves a strip of empty card down the
  // right-hand side — which is what "the columns must use the full width"
  // was pointing at.
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const read = () => setBoxW(el.clientWidth);
    read();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- the divider between the columns and the chart -------------------------
  // Remembered as a FRACTION, so the same reader gets the same proportion on a
  // different screen — see ../shared/gridFit.
  const [splitFrac, setSplitFrac] = useState(() => {
    try { return Number(localStorage.getItem(`grids.${storeKey}.split`)) || 0; } catch { return 0; }
  });
  const [splitDrag, setSplitDrag] = useState(null);
  // WHAT IS REMEMBERED IS WRITTEN FROM A REF, NOT FROM INSIDE A setState.
  // A state updater must be pure — React is entitled to run it twice, and in
  // development it does — so the moment `localStorage.setItem` lived inside
  // one, "save where the divider is" and "put it back where it started"
  // stopped having a reliable order between them.
  const splitRef = useRef(splitFrac);
  const rememberSplit = (f) => {
    splitRef.current = f;
    setSplitFrac(f);
    try {
      if (f > 0) localStorage.setItem(`grids.${storeKey}.split`, String(f));
      else localStorage.removeItem(`grids.${storeKey}.split`);
    } catch { /* storage may be blocked */ }
  };
  const configW = totalWidth(shownCols);
  // Until a reader moves it: the columns' own width if that leaves the chart
  // room, otherwise a little over half. Not a blind 50/50 — five narrow
  // columns should not be stretched across half a screen to make a point.
  const wantSplit = splitFrac > 0
    ? splitPixels(splitFrac, boxW)
    // A phone gets a third of its width for the chart and the rest for the
    // names, because a bar with no name against it is decoration.
    : Math.min(configW, Math.round(boxW * 0.55));
  // THE DIVIDER GOES WHERE THE READER PUTS IT, in both directions.
  //
  // It was capped at the columns' own configured total, to stop a stale saved
  // split ballooning them across the grid. That cap also made the pane
  // impossible to WIDEN, which is worse: a reader who wants to read long
  // summaries beside their bars could not have them. The cap is gone; what
  // fixed the ballooning is the DEFAULT below — `min(configW, ...)` — so a
  // reader who has never touched the divider gets the columns at exactly the
  // width the admin set, and everything past that is the chart's.
  const labelW = timeline && boxW > 0 ? clampSplit(wantSplit, { cols: shownCols, boxW }) : 0;
  // The "+" that adds a column sits to the RIGHT of the headings, so with the
  // columns filling the box to the pixel it would be pushed off the end of a
  // grid that no longer has anywhere to scroll to. Its width is kept back.
  const ADD_W = 38;
  const avail = timeline ? labelW : Math.max(0, boxW - ADD_W);
  // THE ONE PLACE THE DRAWN WIDTHS COME FROM. The config's widths are a
  // shape; these are that shape at this size. `shrink` only with a chart
  // beside them — on its own a wide table scrolls sideways, which is what a
  // reader expects, rather than squeezing thirteen columns into a phone.
  // A width the reader set is LOCKED: drawn exactly, and left out of the
  // arithmetic, so dragging one heading no longer moves every other column.
  // THE TREE COLUMN ASKS FOR THE WIDTH ITS TEXT NEEDS, measured over the rows
  // on screen, and the fit takes that before it shares the slack — so the
  // page opens with its summaries whole rather than "…"-ed at the width the
  // config happened to guess. Capped at a share of the grid, because one
  // very long summary should not take the whole page. Only without the
  // chart: beside a timeline the columns are squeezed to the divider, and a
  // reader who wants more of them there drags it.
  const treeCol = shownCols.find((c) => c.kind === 'summary');
  const want = useMemo(() => {
    if (!treeCol || timeline || !(avail > 0)) return null;
    const cap = Math.max(treeCol.width, Math.floor(avail * 0.6));
    return { [treeCol.id]: treeColumnWidth(rows, measureSummary, { cap }) };
  }, [rows, treeCol, timeline, avail]);
  // WITHOUT THE CHART, THE COLUMNS HAVE THE PAGE AND FILL IT — UNTIL A
  // READER SIZES ONE, exactly as beside the chart.
  //
  // Untouched, the set grows to the page, so a wide screen is not drawn half
  // blank. But the lock used to apply only beside a timeline: with the chart
  // shut every column stayed stretched to fill the page, so widening one
  // took the pixels from all the others and they shrank under the reader —
  // the columns "squashing" each other. Now the rule is the same either way:
  // once a width has been set, every column is drawn at its own width, the
  // others do not move, and a set wider than the page scrolls sideways.
  // `fill` — THE ONE COLUMN THAT TAKES UP THE SLACK once widths are set.
  // With the chart open it is the Summary: it gives way so the columns end at
  // the divider whatever screen the widths were set on, and takes up the room
  // when the divider is dragged (a column dragged moves the divider with it;
  // see the drag). With the chart shut it is the LAST column: on a screen
  // wider than the columns it takes the room left over, so the grid fills the
  // screen — and because it sits to the right of every other column, a column
  // dragged still moves its own edge and nothing to its left. Columns wider
  // than the screen are never squeezed: the grid scrolls.
  const fillId = timeline
    ? (treeCol ? treeCol.id : null)
    : (shownCols.length ? shownCols[shownCols.length - 1].id : null);
  const fitted = useMemo(
    () => fitColumns(shownCols, avail, { shrink: timeline, locked: view.widths, want, fill: fillId }),
    [shownCols, avail, timeline, view.widths, want, fillId],
  );
  const fittedW = totalWidth(fitted);
  const fitScale = configW > 0 ? fittedW / configW : 1;

  useEffect(() => {
    if (!splitDrag) return undefined;
    const move = (e) => {
      const px = clampSplit(splitDrag.w0 + (e.clientX - splitDrag.x0), { cols: shownCols, boxW });
      const f = splitFraction(px, boxW);
      splitRef.current = f;
      setSplitFrac(f);
    };
    // Remembered when the mouse is LET GO, not on every pixel of the drag —
    // localStorage on every mousemove is a write per frame for one answer.
    const up = () => { setSplitDrag(null); rememberSplit(splitRef.current); };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, [splitDrag, shownCols, boxW, configW, storeKey]);

  // ---- selecting rows -----------------------------------------------------------
  // The anchor is the row last ticked; a Shift-click or a Shift+arrow ticks
  // from it. Rows are walked in the order on screen, work items only — a
  // folder cannot be selected and is stepped over.
  const anchor = useRef(null);
  // `add` true always ticks, false always unticks, undefined toggles.
  const pickRow = (r, { shift = false, add = undefined } = {}) => {
    if (!onSelect || !r.it) return;
    const next = new Set(selected || []);
    const a = anchor.current;
    if (shift && a) {
      const i = shown.findIndex((x) => x.rowId === a);
      const j = shown.findIndex((x) => x.rowId === r.rowId);
      if (i >= 0 && j >= 0) {
        for (let k = Math.min(i, j); k <= Math.max(i, j); k += 1) if (shown[k].it) next.add(shown[k].key);
      } else next.add(r.key);
    } else if (add === true || (add !== false && !next.has(r.key))) next.add(r.key);
    else next.delete(r.key);
    anchor.current = r.rowId;
    onSelect(next, r.rowId);
    // The keyboard then works from here without a second click.
    try { if (scroller.current) scroller.current.focus({ preventScroll: true }); } catch { /* an old browser; the mouse still works */ }
  };
  const stepSelection = (dir) => {
    if (!onSelect || !(selected && selected.size)) return false;
    const a = anchor.current;
    let i = shown.findIndex((x) => x.rowId === a);
    if (i < 0) return false;
    for (i += dir; i >= 0 && i < shown.length; i += dir) {
      if (!shown[i].it) continue;
      const next = new Set(selected);
      next.add(shown[i].key);
      anchor.current = shown[i].rowId;
      onSelect(next, shown[i].rowId);
      // Keep the row that was just ticked in view.
      const el = scroller.current;
      if (el) {
        const y = HEAD_H + i * rowHeight;
        if (y < el.scrollTop + HEAD_H) el.scrollTop = y - HEAD_H;
        else if (y + rowHeight > el.scrollTop + el.clientHeight) el.scrollTop = y + rowHeight - el.clientHeight;
      }
      return true;
    }
    return false;
  };
  const onKeys = (e) => {
    stopGlide();
    if (e.key === 'Escape' && focusAt) setFocusAt(null);
    if (e.shiftKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      if (stepSelection(e.key === 'ArrowDown' ? 1 : -1)) e.preventDefault();
    }
  };

  // ---- the pinned columns -------------------------------------------------------
  // The page's pinned columns (Key and Summary unless an admin chose others),
  // wherever they sit in the order, each stuck at the width of the pinned
  // ones before it. A reader's own column is never pinned. Only without the chart: beside a timeline
  // the whole column pane is already pinned and the columns are fitted to it.
  // They never take more than 85% of the box — on a phone a pinned
  // 340px summary would leave nothing to scroll; the ones that do not fit
  // simply scroll with the rest.
  const frozen = useMemo(() => {
    if (timeline) return null;
    const want = frozenIds ? new Set(frozenIds) : null;
    const room = boxW > 0 ? boxW * 0.85 : Infinity;
    const leftOf = {};
    let x = 0;
    let last = null;
    const skipped = new Set();
    for (const c of fitted) {
      if (c.mine || !(want ? want.has(c.id) : (c.kind === 'key' || c.kind === 'summary'))) continue;
      if (skipped.size || x + c.width > room) { skipped.add(c.id); continue; }
      leftOf[c.id] = x;
      x += c.width;
      last = c.id;
    }
    return last || skipped.size ? { left: leftOf, last, skipped } : null;
  }, [frozenIds, timeline, fitted, boxW]);

  const { paneW, renderRow, colStyle } = makeRowRenderer({
    cols: fitted, ctx: ctxAll, ui, S, workTypes, isDark, editable, onToggle, onOpenItem, onEdit, savingKeys,
    api, onCreated, onStatusChanged, renaming, onRename: setRenaming, onEditRule, onRemoveRule, itemHref, narrow,
    selected, onPick: onSelect ? pickRow : null, cloneable, onDraft, draft,
    onMoveFolder, dragFolder, dropOn, setDropOn,
    frozen, scrolledX: left > 0,
  });

  // ---- the timeline pane ------------------------------------------------------
  // Its geometry comes off the SAME rows the grid draws, so a bar cannot
  // drift out of line with its own cells.
  const [localGrain, setLocalGrain] = useState(() => {
    try { return localStorage.getItem(`grids.${storeKey}.grain`) || ''; } catch { return ''; }
  });
  const win = useMemo(() => (timeline && dateWindow) || windowOf(timeline ? rows : []), [rows, timeline, dateWindow]);
  const useGrain = grainFor(win, ganttGrain(grain || localGrain));
  const px = PX[useGrain] || PX.week;
  const chartW = timeline ? chartWidth(win, px) : 0;
  const ticks = useMemo(() => (timeline ? ticksOf(win, useGrain, px) : []), [timeline, win, useGrain, px]);
  // The merged row above the columns: the month over the days, the year over
  // the weeks and the months.
  const supers = useMemo(() => (timeline ? superTicks(win, useGrain, px) : []), [timeline, win, useGrain, px]);
  const bands = useMemo(() => (timeline ? weekendBands(win, useGrain, px) : []), [timeline, win, useGrain, px]);
  const tX = timeline ? todayX(win, px) : 0;
  const setGrain = (g) => {
    setLocalGrain(g);
    if (onGrain) onGrain(g);
    try { localStorage.setItem(`grids.${storeKey}.grain`, g); } catch { /* storage may be blocked */ }
  };
  const BAR = { planned: ui.blue, late: '#dc2626', done: '#4cc38a' };

  // ---- a bar is a handle ------------------------------------------------------
  // Only a WORK ITEM's bar can be dragged. A folder's or a group's bar is the
  // span of everything beneath it — an aggregate has no dates of its own to
  // write, and moving it would have to guess which of forty children meant it.
  const canDragBars = !!(timeline && editable && onTimelineEdit);
  const bdrag = useRef(null);            // the live drag; a ref so the document
  const [bview, setBview] = useState(null);  // listeners never read a stale one
  // A link being drawn by hand: { key, side, x, y } — the bar it started
  // from, which end, and where that end is in the chart. Null the rest of
  // the time, which is also what says the dots are idle.
  const [linkDrag, setLinkDrag] = useState(null);
  const linkRef = useRef(null);
  // Every dependency edge among the rows, for the gesture's listeners (they
  // run after the render that filled it; see `depAll` below).
  const depAllRef = useRef([]);
  // Declared HERE, with the other things the whole component reads, because
  // the bar renderer below wants it too. It depends on props alone, so there
  // is nothing to wait for — and nothing left that reads it before it exists.
  const depRules = (timeline && dependencies) || null;
  // The kinds a hand-drawn link may be: only those this grid names a link
  // type for, because that link type is what gets written. None, no dots.
  const linkKinds = depRules ? Object.keys(depRules).filter((k) => depRules[k] && depRules[k].linkTypeName) : [];
  const canLink = !!(timeline && editable && onLink && linkKinds.length && !narrow);
  // PRESS A DOT: the link starts there. Where that dot is in the CHART is
  // read off the cell the bar is drawn in (its left edge is the chart's x = 0)
  // and the row's place in the list, so the line drawn while dragging starts
  // exactly on it whatever the scroll.
  const startLink = (e, key, side, rowIndex) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const dot = e.currentTarget.getBoundingClientRect();
    const cell = e.currentTarget.closest('[data-chart-cell]');
    const box = cell ? cell.getBoundingClientRect() : dot;
    const from = { key, side, x: dot.left + dot.width / 2 - box.left, y: rowIndex * rowHeight + rowHeight / 2 };
    linkRef.current = from;
    setLinkDrag(from);
  };
  // While a link is being drawn, the ends it could FINISH on: a kind this
  // grid names, and never a second link between the same two work items
  // (one dependency per pair, as in the folders Gantt). The bar it started
  // from keeps only the dot it started from.
  const linkTargetOk = (key, side) => {
    const from = linkDrag;
    if (!from) return true;
    if (key === from.key) return side === from.side;
    const kind = deriveType(from.side, side);
    return !!kind && linkKinds.includes(kind) && !linkedToFrom.has(key);
  };
  const justDragged = useRef(false);
  const dragging = !!bview;

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => {
      const d = bdrag.current;
      if (!d) return;
      d.dx = e.clientX - d.x0;
      if (Math.abs(d.dx) > SLOP) d.moved = true;
      setBview({ rowId: d.rowId, key: d.row && d.row.key, handle: d.handle, dx: d.dx, moved: d.moved });
    };
    const up = () => {
      const d = bdrag.current;
      bdrag.current = null;
      setBview(null);
      if (!d) return;
      justDragged.current = !!d.moved;
      if (!d.moved) return;
      const next = datesAfterDrag({
        start: d.row.start, due: d.row.due, handle: d.handle, days: daysFromDx(d.dx, px),
      });
      // What depends on the moved bar follows it — that is the page's job
      // (pushDependents in Structure.jsx), once this write has landed. The
      // grip goes with it: only a WHOLE move ('move') may take the work
      // under the bar along (the Rescheduler).
      if (next) onTimelineEdit(d.row, next, { handle: d.handle });
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, [dragging, px, onTimelineEdit]);

  const startBarDrag = (e, r, bar) => {
    if (!canDragBars || !r.it) return;
    e.preventDefault();
    e.stopPropagation();
    // MEASURED OFF THE BAR'S OWN BOX, not `offsetX`. That is relative to
    // whatever element the pointer is actually over, which is not reliably
    // the one the handler is on.
    const box = e.currentTarget.getBoundingClientRect();
    const handle = bar.kind === 'dot' ? 'move' : handleAt(e.clientX - box.left, box.width);
    bdrag.current = { rowId: r.rowId, row: r, handle, x0: e.clientX, dx: 0, moved: false };
    setBview({ rowId: r.rowId, key: r.key, handle, dx: 0, moved: false });
    // A bar that carries the work under it: make sure the page knows what
    // that work is before the hand lets go, so the preview can show it move.
    if (handle === 'move' && bar.kind === 'bar' && onRescheduleIntent && reschedulable && reschedulable(r.it)) onRescheduleIntent(r);
  };
  // THE WORK THAT MOVES WITH A BAR DRAGGED WHOLE, drawn moving with it while
  // the hand is still down — the Rescheduler's plan for that bar, the keys
  // it would move. Null when nothing follows.
  const followers = useMemo(() => {
    if (!bview || bview.handle !== 'move' || !bview.key || !rescheduleInfo) return null;
    const info = rescheduleInfo(bview.key);
    const plan = info && info.plan;
    if (!plan || !Array.isArray(plan.items)) return null;
    return new Set(plan.items.filter((x) => x.move).map((x) => x.key));
  }, [bview && bview.key, bview && bview.handle, rescheduleInfo]);
  // Hovering a bar that carries the work under it reads that work — after a
  // moment, so sweeping the mouse across the chart reads nothing.
  const intentTimer = useRef(0);
  const barIntent = (r, on) => {
    if (intentTimer.current) { clearTimeout(intentTimer.current); intentTimer.current = 0; }
    if (on && onRescheduleIntent) intentTimer.current = setTimeout(() => { intentTimer.current = 0; onRescheduleIntent(r); }, 350);
  };
  useEffect(() => () => { if (intentTimer.current) clearTimeout(intentTimer.current); }, []);
  // A drag ends in a mouseup, and a mouseup on the thing you pressed is also
  // a CLICK — so without this, every nudge of a bar also opened the work item.
  const barClick = (r) => {
    if (justDragged.current) { justDragged.current = false; return; }
    if (r.it) onOpenItem(r.it); else onToggle(r.path);
  };

  // What the bar looks like MID-DRAG: snapped to whole days, so the reader can
  // see they are moving it three days and not "a bit".
  const previewBar = (r, bar) => {
    const d = bview && bview.rowId === r.rowId && bview.moved ? bview : null;
    if (!d) return { x: bar.x, w: bar.w, days: 0 };
    const days = daysFromDx(d.dx, px);
    const snap = days * px;
    if (d.handle === 'start') return { x: bar.x + snap, w: Math.max(px, bar.w - snap), days };
    if (d.handle === 'end') return { x: bar.x, w: Math.max(px, bar.w + snap), days };
    return { x: bar.x + snap, w: bar.w, days };
  };

  // ---- a bar that is not on screen -------------------------------------------
  // The chart scrolls sideways and the rows do not, so a reader can be looking
  // straight at a row whose bar is two thousand pixels away. `viewW` is the
  // chart's visible width — the box less the frozen columns pinned over its
  // left-hand edge — and `left` (the scroller's own scrollLeft) is the
  // chart-local x of that view's left edge, because the pane is pinned there.
  const viewW = Math.max(0, boxW - paneW);
  // THE CHART IS DRAWN ONLY WHERE IT CAN BE SEEN, ACROSS AS WELL AS DOWN.
  //
  // The rows were windowed; the WIDTH never was. A day-grain window over a
  // year and a half is fourteen thousand pixels, and the backdrop and the
  // date heading drew every tick and every weekend band across all of it —
  // some seven hundred spans apiece, while a screen shows two thousand pixels
  // of them. The cost was never the arithmetic. It was the area.
  //
  // Rounded out to whole bands, so a scroll re-slices on crossing one rather
  // than on every pixel, and over-drawn either side so nothing arrives late.
  const X_BLOCK = 1200;
  const xFrom = Math.floor((left - X_BLOCK) / X_BLOCK) * X_BLOCK;
  const xTo = Math.ceil((left + viewW + X_BLOCK) / X_BLOCK) * X_BLOCK;
  const inX = (x, w = 0) => x + w >= xFrom && x <= xTo;
  useEffect(() => {
    if (!onChartView) return;
    if (!timeline || !(px > 0)) { onChartView(null); return; }
    onChartView({
      grain: useGrain,
      from: addDays(win.from, Math.max(0, Math.floor(left / px))),
      days: Math.max(1, Math.ceil(viewW / px)),
    });
  }, [onChartView, timeline, left, px, viewW, win.from, useGrain]);
  // The button is about 110px wide and sits 10px right of the anchor; anything
  // inside this of the anchor is "still heading for it", not "somewhere else".
  const JUMP_HOLD = 150;
  const [jump, setJump] = useState(null);   // { rowId, x } — where the mouse came in
  // THE ROW (AND CELL) LAST CLICKED, as a structure shows it: the row stays
  // tinted blue and the cell is outlined, while the hover tint goes on
  // following the mouse. { rowId, colId } — colId null for a click in the
  // chart. Escape lets it go. Nothing is selected for any action by this;
  // the checkboxes are still how rows are picked for Bulk Edit.
  const [focusAt, setFocusAt] = useState(null);
  const focusFrom = (e, r) => {
    if (r.kind === 'draft' || r.kind === 'rule' || e.button !== 0) return;
    const cell = e.target && e.target.closest ? e.target.closest('[data-col]') : null;
    const colId = cell ? cell.getAttribute('data-col') : null;
    setFocusAt((p) => (p && p.rowId === r.rowId && p.colId === colId ? p : { rowId: r.rowId, colId }));
  };
  // A CLICK ANYWHERE ELSE LETS IT GO: outside the grid, on its headings, on
  // the empty space below the rows. A click on another row moves it there
  // (that row's own capture handles it), and a press on the grid's own
  // scrollbars — whose target is the scroller itself — leaves it alone.
  useEffect(() => {
    if (!focusAt) return undefined;
    const away = (e) => {
      const t = e.target;
      const box = scroller.current;
      if (box && t === box) return;
      if (box && t && box.contains(t) && t.closest && t.closest('.wip-row')) return;
      setFocusAt(null);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [focusAt]);
  // IT GLIDES, AND THE GLIDE IS OURS — the maths is in ../shared/glide, which
  // also says why `scrollTo({ behavior: 'smooth' })` is not used (it is a
  // request, and where it is refused NOTHING moves at all).
  //
  // Here is only the plumbing: run the frames, land whatever happens, and let
  // the reader cancel at any moment — nobody should have to wait out an
  // animation to scroll their own page.
  const glide = useRef(0);
  const glideEnd = useRef(0);
  const stopGlide = () => {
    if (glide.current) { cancelAnimationFrame(glide.current); glide.current = 0; }
    if (glideEnd.current) { clearTimeout(glideEnd.current); glideEnd.current = 0; }
  };
  useEffect(() => stopGlide, []);
  const scrollXTo = (to, animate = true) => {
    const el = scroller.current;
    if (!el) return;
    stopGlide();
    el.style.scrollBehavior = 'auto';       // ours, not the browser's
    const from = el.scrollLeft;
    const d = to - from;
    const land = () => { stopGlide(); el.scrollLeft = to; putLeft(to); };
    if (!shouldGlide(d, { animate, reduceMotion: prefersReducedMotion() })) { land(); return; }
    const ms = glideMs(d);
    const clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const t0 = clock();
    const step = () => {
      const elapsed = clock() - t0;
      if (elapsed >= ms) { land(); return; }
      el.scrollLeft = glidePosition(from, to, elapsed, ms);
      glide.current = requestAnimationFrame(step);
    };
    glide.current = requestAnimationFrame(step);
    // AND IT ARRIVES EVEN IF THE FRAMES DO NOT — see LAND_GRACE.
    glideEnd.current = setTimeout(land, ms + LAND_GRACE);
  };
  const jumpTo = (bar) => {
    scrollXTo(scrollTargetFor(bar, { viewW, chartW }));
    setJump(null);
  };
  const toToday = (animate = true) => {
    if (!(viewW > 0)) return;
    scrollXTo(Math.max(0, Math.min(chartW - viewW, tX - viewW * 0.35)), animate);
  };
  // IT OPENS ON TODAY. A structure holding one job that closed in March and
  // one that lands next June has a chart fifteen months wide, and opening it
  // at the left-hand edge shows the reader March — which is nobody's question.
  // Only on the way IN (the timeline switching on, or the grain changing under
  // them); after that the scroll is theirs and is left alone.
  const centred = useRef('');
  useEffect(() => {
    if (!timeline || !(viewW > 0) || !chartW) return;
    const mark = `${useGrain}:${win.from}:${win.to}`;
    if (centred.current === mark) return;
    centred.current = mark;
    // ARRIVING is not TRAVELLING: the chart is being drawn for the first
    // time, so there is nothing yet for a glide to show the reader moving
    // away from. It opens on today, it does not slide there.
    toToday(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeline, useGrain, win.from, win.to, viewW, chartW]);

  // ---- dragging the chart itself ---------------------------------------------
  // THE EMPTY SPACE IS A HANDLE TOO. A chart this wide is mostly gaps between
  // bars, and reaching for a horizontal scrollbar at the bottom of the window
  // to move it is the long way round when the thing you want to move is right
  // under the pointer. Press anywhere that is not a bar and drag.
  //
  // It does not fight the bar drag: a bar stops the event before it gets
  // here, so the two never both start.
  const pan = useRef(null);
  const [panning, setPanning] = useState(false);
  const startPan = (e) => {
    if (!timeline || e.button !== 0) return;
    const el = scroller.current;
    if (!el) return;
    stopGlide();
    pan.current = { x0: e.clientX, left0: el.scrollLeft };
    setPanning(true);
  };
  useEffect(() => {
    if (!panning) return undefined;
    const move = (e) => {
      const d = pan.current;
      const el = scroller.current;
      if (!d || !el) return;
      // Dragging the chart LEFT moves the view forward in time, which is what
      // dragging a piece of paper does.
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = d.left0 - (e.clientX - d.x0);
    };
    const up = () => {
      const el = scroller.current;
      pan.current = null;
      setPanning(false);
      if (el) putLeft(el.scrollLeft);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
  }, [panning]);

  const timelineCell = (row0, rowIndex) => {
    if (row0.kind === 'rule') return <div style={{ width: chartW, height: '100%' }} onMouseDown={startPan} />;
    // An aggregate's dates arrive here, at draw time, and only for the thirty
    // rows in view.
    const span = !row0.it && spanOfRow ? spanOfRow(row0) : null;
    const r = span ? { ...row0, ...span } : row0;
    // CLIPPED TO THE CHART. An aggregate's span can reach outside the window
    // (its shut rows are not on screen, so they are not in the window), and
    // a bar drawn at a negative x runs under the frozen columns. What is
    // drawn stops at the chart's edge; the tooltip still says the real dates.
    const bar = clipBar(barOf(r, win, px), chartW);
    const tone = toneOf(r, win.today);
    const c = BAR[tone];
    const tail = tailOf(r, bar, win, px, win.today);
    const h = barHeightOf(r);
    const off = offView(bar, { scrollLeft: left, viewW });
    const draggable = canDragBars && !!r.it;
    const moved = bview && bview.rowId === r.rowId && bview.moved;
    // CARRIED ALONG: a bar under one being dragged whole by the Rescheduler
    // slides with it, the same number of days, while the hand is down.
    const carried = !!(bar && !moved && followers && r.it && bview && bview.moved && followers.has(r.key));
    const live = bar ? (carried ? { x: bar.x + daysFromDx(bview.dx, px) * px, w: bar.w, days: daysFromDx(bview.dx, px) } : previewBar(r, bar)) : null;
    // The admin's rule for this bar's work type, when its bars carry the work
    // under them — a whole bar with both dates, that the reader may drag.
    const rsRule = draggable && bar && bar.kind === 'bar' && reschedulable ? reschedulable(r.it) : null;
    const next = moved
      ? datesAfterDrag({ start: r.start, due: r.due, handle: bview.handle, days: daysFromDx(bview.dx, px) })
      : null;
    const showJump = !!(bar && off && jump && jump.rowId === r.rowId);
    return (
      <div data-chart-cell="" style={{ width: chartW, height: '100%', position: 'relative', overflow: 'hidden', cursor: panning ? 'grabbing' : 'default' }}
        onMouseDown={startPan}
        onMouseMove={off && bar && !dragging ? (e) => {
          // WHERE THE MOUSE IS IN THE CHART, measured off the cell's own box.
          // `offsetX` is relative to whatever element the pointer happens to
          // be over — which here is usually one of the zero-width tick lines,
          // so it read as 3 wherever the mouse really was and the button
          // appeared pinned to the left-hand edge instead of at the cursor.
          const box = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - box.left;
          // IT APPEARS AT THE MOUSE AND THEN HOLDS STILL. Re-placing it on
          // every few pixels of travel meant the mouse pushed it along the row
          // ahead of itself and the button could never actually be reached —
          // moving towards it moved it. So it is anchored where the pointer
          // came in, and only re-anchors on a real move somewhere else, which
          // is further than the button is wide.
          setJump((p) => (p && p.rowId === r.rowId && Math.abs(p.x - x) < JUMP_HOLD ? p : { rowId: r.rowId, x }));
        } : undefined}
        onMouseLeave={off && bar ? () => setJump((p) => (p && p.rowId === r.rowId ? null : p)) : undefined}>
        {/* THE GRIDLINES ARE NOT HERE ANY MORE — see `chartBackdrop`. They
            were drawn per row, which at day grain over a fifteen-month window
            is 450 spans × 30 visible rows = thirteen THOUSAND absolutely
            positioned elements, rebuilt on every scroll tick. That one line
            was most of "the Gantt makes everything slow". They are now one
            layer behind every row, drawn once. */}
        {tail ? <span style={{ position: 'absolute', left: tail.x, width: tail.w, top: '50%', height: 2, marginTop: -1, background: '#dc2626', opacity: 0.55 }} /> : null}
        {bar && bar.kind === 'bar' ? (
          <span className="wip-bar" title={`${r.summary || r.key}\n${bar.from} → ${bar.to}${bar.inverted ? '\nDUE BEFORE IT STARTS' : ''}${tone === 'late' ? '\nPAST ITS DUE DATE' : ''}${bar.cutStart || bar.cutEnd ? '\n(the bar runs past the edge of the chart)' : ''}${draggable ? '\nDrag to move it, drag an edge to change one date' : ''}${rsRule ? '\nDragged whole, the work under it moves with it (Rescheduler)' : ''}`}
            onMouseDown={draggable ? (e) => startBarDrag(e, r, bar) : undefined}
            onMouseEnter={rsRule ? () => barIntent(r, true) : undefined}
            onMouseLeave={rsRule ? () => barIntent(r, false) : undefined}
            onClick={() => barClick(r)}
            style={{
              position: 'absolute', left: live.x, width: live.w, top: `calc(50% - ${h / 2}px)`, height: h,
              background: r.isGroup ? `${c}55` : c, border: `1px solid ${bar.inverted ? '#f5a623' : c}`,
              // A bar cut at the chart's edge is drawn square there, so the
              // eye reads "continues" rather than "ends here".
              borderRadius: `${bar.cutStart ? 0 : 4}px ${bar.cutEnd ? 0 : 4}px ${bar.cutEnd ? 0 : 4}px ${bar.cutStart ? 0 : 4}px`, boxSizing: 'border-box',
              cursor: draggable ? cursorFor(moved ? bview.handle : 'move') : 'pointer',
              opacity: moved || carried ? 0.75 : 1,
              outline: carried ? `1.5px dashed ${ui.blue}` : undefined, outlineOffset: carried ? 1 : undefined,
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
            {/* The key, only when the bar is wide enough to hold it whole. A
                clipped half-key is noise, and pointer-events stay off so the
                bar is still a handle along its whole length. */}
            {!r.isGroup && live.w >= 46 ? (
              <span style={{
                fontFamily: FONT, fontSize: 10, fontWeight: 700, letterSpacing: 0.2,
                color: '#fff', whiteSpace: 'nowrap', pointerEvents: 'none', opacity: 0.95,
              }}>{r.key}</span>
            ) : null}
            {/* THE TWO ENDS, AS HANDLES — the folders Gantt's dots, in the
                folders Gantt's places: INSIDE the bar, DOT_INSET from each
                end, which is also where an arrow enters it. Outside the bar
                they would be cut in half by its own overflow. Shown when the
                pointer is on the bar; while a link is being drawn, shown on
                every bar it could finish on and on no other. A bar too narrow
                to hold two dots clear of each other has none. */}
            {canLink && !r.isGroup && r.it && live.w >= DOT_MIN_BAR ? ['start', 'finish'].map((side) => (
              linkDrag && !linkTargetOk(r.key, side) ? null : (
                <span key={side}
                  className={`wip-dep-dot${linkDrag ? ' wip-dep-on' : ''}`}
                  data-dep-key={r.key}
                  data-dep-side={side}
                  title={side === 'start' ? 'Drag from here to link the start of this work item' : 'Drag from here to link the finish of this work item'}
                  onMouseDown={(e) => startLink(e, r.key, side, rowIndex)}
                  // A click on a dot is not a click on the bar (which opens it).
                  onClick={(e) => e.stopPropagation()}
                  // The folders Gantt's dot exactly (.gantt-bar-dep-dot): six
                  // pixels of the accent, a hairline white ring to lift it
                  // off any bar colour, and a soft shadow.
                  style={{
                    position: 'absolute', top: 'calc(50% - 3px)', width: 6, height: 6,
                    [side === 'start' ? 'left' : 'right']: dotInsetFor(live.w) - 3, borderRadius: '50%',
                    background: `var(--accent, ${ui.blue})`,
                    boxShadow: '0 0 0 1px #fff, 0 1px 2px rgba(0, 0, 0, 0.4)',
                    // NO z-index. It is inside the bar, so it is drawn over
                    // the bar anyway; a z-index lifted it over the pinned
                    // columns too (they sit at 1), and a bar running under
                    // them showed its dot through the cells.
                    cursor: 'crosshair',
                  }} />
              )
            )) : null}
          </span>
        ) : null}
        {bar && bar.kind === 'dot' ? (
          <span title={`${r.summary || r.key}\n${bar.onlyStart ? 'Starts' : 'Due'} ${bar.from} — no ${bar.onlyStart ? 'due' : 'start'} date${draggable ? '\nDrag to move it' : ''}`}
            onMouseDown={draggable ? (e) => startBarDrag(e, r, bar) : undefined}
            onClick={() => barClick(r)}
            style={{
              position: 'absolute', left: live.x - 5, top: 'calc(50% - 5px)', width: 10, height: 10,
              background: c, transform: 'rotate(45deg)', cursor: draggable ? 'grab' : 'pointer',
              opacity: moved || carried ? 0.75 : 1,
              outline: carried ? `1.5px dashed ${ui.blue}` : undefined,
            }} />
        ) : null}
        {/* THE RESCHEDULER'S MARK, beside a bar whose work type carries the
            work under it: hover it for what a whole drag will move. Just
            right of the bar's end — and, while any of the bar is in view,
            never outside the part of the chart that is on screen: a long
            bar running off the edge keeps its mark at that edge, over the
            bar. */}
        {rsRule && !moved && !off ? (
          <RescheduleBadge ui={ui} row={r} rule={rsRule}
            info={rescheduleInfo ? rescheduleInfo(r.key) : null} onIntent={onRescheduleIntent}
            active={!!(savingKeys && savingKeys.has(r.key))}
            style={{ left: Math.max(left + 4, Math.min(live.x + live.w + 5, left + Math.max(26, viewW) - 24, chartW - 20)) }} />
        ) : null}
        {/* THE DATES THE DRAG WILL WRITE, said out loud while it is happening.
            A bar that slides without telling you where it landed is a gamble. */}
        {moved && next ? (
          <span style={{
            position: 'absolute', left: Math.max(2, live.x), top: 1, zIndex: 3,
            background: ui.card, border: `1px solid ${ui.primary}`, borderRadius: 4,
            padding: '0 5px', fontSize: 10, fontWeight: 800, color: ui.fg, whiteSpace: 'nowrap',
            fontFamily: FONT, pointerEvents: 'none',
          }}>{next.start || r.start || '—'} → {next.due || r.due || '—'}</span>
        ) : null}
        {/* SCROLL TO TASK, at the mouse. The row is here; its bar is not. Which
            way, and one click to go — clamped into the visible strip of chart,
            because a button off the right-hand edge is no better than the bar
            it is offering to find. */}
        {showJump ? (
          <button onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); jumpTo(bar); }}
            title={`${r.summary || r.key}\n${bar.from}${bar.to !== bar.from ? ` → ${bar.to}` : ''} — ${off < 0 ? 'behind' : 'ahead of'} the view`}
            style={{
              position: 'absolute', zIndex: 4, height: 20, top: 'calc(50% - 10px)',
              left: Math.min(Math.max(left + 4, jump.x + 10), left + Math.max(4, viewW - 128)),
              display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 8px',
              border: `1px solid ${ui.primary}`, borderRadius: 999, background: ui.card, color: ui.fg,
              fontSize: 10.5, fontWeight: 800, fontFamily: FONT, cursor: 'pointer',
              boxShadow: '0 2px 10px rgba(0,0,0,0.22)', whiteSpace: 'nowrap',
            }}>{off < 0 ? '◀' : ''} Scroll to task {off > 0 ? '▶' : ''}</button>
        ) : null}
      </div>
    );
  };

  // ---- the axis, in two tiers -------------------------------------------------
  // A date column carries two facts and only one of them changes often. The
  // slow one — the year over the weeks, the month over the days — is MERGED
  // across the span it covers; the fast one gets its own column. Printing both
  // in every column spent the width on the half that was the same for thirty
  // columns running, and then truncated the half that was not.
  const SUPER_H = 16;
  const tickHeader = (
    <div style={{ width: chartW, height: '100%', position: 'relative' }}>
      {/* the merged tier: 2026 · 2027, or Sep 26 · Oct 26 over the days.
          Both tiers are drawn only across the visible stretch (xFrom/xTo). */}
      {supers.filter((t) => inX(t.x, t.w)).map((t) => (
        <span key={`s${t.key}`} style={{
          position: 'absolute', left: t.x, width: t.w, top: 0, height: SUPER_H,
          borderLeft: `1px solid ${ui.border}`, borderBottom: `1px solid ${ui.border}`,
          background: ui.surface, fontSize: 10, fontWeight: 800, letterSpacing: '0.04em',
          color: ui.fg, fontFamily: FONT, display: 'flex', alignItems: 'center',
          // Centred, because a merged heading belongs to the whole span it
          // covers — but it sticks to the left edge once the span is wide
          // enough that a centred label drifts away from its own boundary.
          justifyContent: t.w > 240 ? 'flex-start' : 'center',
          paddingLeft: t.w > 240 ? 6 : 0, overflow: 'hidden', whiteSpace: 'nowrap',
        }}>{t.w > 26 ? t.label : ''}</span>
      ))}
      {ticks.filter((t) => inX(t.x, t.w)).map((t) => (
        <span key={t.key} style={{
          position: 'absolute', left: t.x, width: t.w, top: SUPER_H, bottom: 0,
          borderLeft: `1px solid ${ui.border}`,
          fontSize: 10, color: t.weekend ? ui.faint : ui.muted, fontFamily: FONT,
          display: 'flex', alignItems: 'center',
          // A day column is 26px and its label is one or two digits, so it is
          // centred under its own stripe; a week or a month column is wide and
          // reads better against its own left-hand edge.
          justifyContent: useGrain === 'day' ? 'center' : 'flex-start',
          paddingLeft: useGrain === 'day' ? 0 : 4,
          background: t.weekend ? `${ui.border}55` : 'transparent',
          overflow: 'hidden', whiteSpace: 'nowrap',
        }}>{t.label}</span>
      ))}
      {/* TODAY IS A MARK, NOT A WORD IN THE WAY.
          The word used to be printed beside the line at the top of the tick
          row — which at Days grain is exactly where the day numbers are, so
          "TODAY" was laid straight over "25 26" and both became unreadable.
          A caret on the line says the same thing, cannot collide with
          anything, and the word now lives in the legend where there is room
          for it. The line carries the date in its tooltip. */}
      <span title={`Today — ${win.today}`}
        style={{ position: 'absolute', left: tX - 1, top: 0, bottom: 0, width: 2, background: '#f5a623' }} />
      <span aria-hidden style={{
        position: 'absolute', left: tX - 5, top: 0, width: 0, height: 0,
        borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
        borderTop: '6px solid #f5a623',
      }} />
    </div>
  );

  // ---- the chart's backdrop ---------------------------------------------------
  // ONE LAYER BEHIND EVERY ROW, drawn once. The gridlines, the weekend bands
  // and the today line used to be rendered inside every row's timeline cell:
  // at day grain over a fifteen-month window that is 450 absolutely positioned
  // spans × 30 visible rows = thirteen THOUSAND elements, rebuilt on every
  // scroll tick. It was most of "the Gantt makes everything slow". Nothing
  // about them varies by row, so nothing about them belongs in a row.
  // TOUCH HAS NO HOVER, so everything this grid reveals on hover has to be
  // there already — the create "+", the rename pencil, the column menu. On a
  // phone they are simply visible; the row is taller to make room for them
  // and to be a thumb-sized target rather than a line of text.
  const touchCss = narrow
    ? '.wip-plus-wrap, .wip-row-tools, .wip-head-menu { opacity: 1 !important; }'
      + ' .wip-scroll { -webkit-overflow-scrolling: touch; }'
    : '';

  // THE WINDOW FIRST, because everything below is measured against it.
  //
  // These were declared BELOW the backdrop, which then read first/last to
  // size itself — a const is not initialised until its declaration runs, so
  // that threw a ReferenceError on the first render and the grid came up
  // blank. Parsing cannot catch it and no suite renders this component, so
  // the build was green and the page was empty.
  //
  // `top` is the scroll position rounded DOWN to a block (see ROW_BLOCK), so
  // the real one is anywhere up to a block further on: the window reaches a
  // block past the view to cover it, plus OVER rows either side to scroll
  // into before the next block's render lands.
  const OVER = 8;
  const at = Math.max(0, top - HEAD_H);
  const first = Math.max(0, Math.floor(at / rowHeight) - OVER);
  const last = Math.min(shown.length, Math.ceil((at + blockPx + viewH) / rowHeight) + OVER);
  const slice = shown.slice(first, last);

  // THE BACKDROP COVERS THE RENDERED WINDOW, NOT EVERY ROW.
  //
  // It used to be top:0 height:100% of the whole row stack — eight hundred
  // rows is twenty-eight THOUSAND pixels, and the gridlines are hundreds of
  // spans each that tall. The browser re-composited the lot on every scroll
  // tick, which is what was left of the jitter once the arrows were windowed;
  // it is also why the lines carried on below the last row. It moves with the
  // slice now, like the rows and the arrows do.
  const backdropTop = first * rowHeight;
  const backdropH = Math.max(0, (last - first) * rowHeight);
  // AND ONLY THE PART OF IT YOU CAN SEE — see xFrom/xTo above. The marks are
  // built once per horizontal band, not once per render: a render between
  // two bands (a block of rows scrolled, a cell edited) hands React the very
  // same elements, and it skips them.
  const backdropMarks = useMemo(() => (timeline ? (
    <>
      {bands.filter((b) => inX(b.x, b.w)).map((b) => (
        <span key={`w${b.key}`} style={{ position: 'absolute', left: b.x, width: b.w, top: 0, bottom: 0, background: ui.border, opacity: 0.28 }} />
      ))}
      {ticks.filter((t) => inX(t.x)).map((t) => (
        <span key={t.key} style={{ position: 'absolute', left: t.x, top: 0, bottom: 0, borderLeft: `1px solid ${ui.border}`, opacity: 0.5 }} />
      ))}
      {supers.filter((t) => inX(t.x)).map((t) => (
        <span key={`s${t.key}`} style={{ position: 'absolute', left: t.x, top: 0, bottom: 0, borderLeft: `1px solid ${ui.border}`, opacity: 0.9 }} />
      ))}
      <span style={{ position: 'absolute', left: tX - 1, top: 0, bottom: 0, width: 2, background: '#f5a623', opacity: 0.85 }} />
    </>
  ) : null), [timeline, bands, ticks, supers, xFrom, xTo, tX, ui.border]); // eslint-disable-line react-hooks/exhaustive-deps
  const chartBackdrop = timeline ? (
    <div aria-hidden style={{
      position: 'absolute', left: paneW, top: backdropTop, width: chartW, height: backdropH,
      pointerEvents: 'none', zIndex: 0,
    }}>
      {backdropMarks}
    </div>
  ) : null;


  // ---- dependency arrows -------------------------------------------------
  //
  // The folders gantt measures its bars off the DOM. That cannot work here:
  // the grid is WINDOWED, so a row outside `slice` has no element to measure
  // and its arrow would vanish on scroll. barOf is pure arithmetic over the
  // row's own dates, so every row's bar is computable whether it is drawn or
  // not — geometry comes from `shown`, not from the page.
  //
  // Rules come from the grid's own config and nowhere else. No rule, no
  // arrows: buildDependencyEdges returns [] and the overlay never mounts.

  // Where each key sits in `shown`. One pass, and it answers both the culling
  // below and the geometry, so neither has to walk the rows again.
  const depRowIndex = useMemo(() => {
    const m = new Map();
    if (!depRules) return m;
    for (let i = 0; i < shown.length; i += 1) {
      const r = shown[i];
      if (r && r.key && !m.has(r.key)) m.set(r.key, i);
    }
    return m;
  }, [shown, depRules]);

  // EVERY edge the rules name. Recomputed only when the rows or the rules
  // change — never on scroll.
  const depAll = useMemo(() => {
    if (!depRules || !Object.keys(depRules).length) return [];
    const items = [];
    const seen = new Set();
    for (const r of shown) {
      if (!r || !r.it || !r.key || seen.has(r.key)) continue;
      seen.add(r.key);
      items.push(r.it);
    }
    return buildDependencyEdges(items, depRules);
  }, [shown, depRules]);

  // THE ARROWS ARE WINDOWED, LIKE THE ROWS.
  //
  // A grid of a few hundred items carries a thousand links, and drawing all
  // of them put a thousand <path> nodes in the page while the rows they point
  // at were virtualised down to forty. That is what made scrolling jitter:
  // the work was not the arithmetic, it was the DOM.
  //
  // An edge is kept when the span between its two rows OVERLAPS the rendered
  // window — not when an endpoint is inside it. A dependency from row 5 to
  // row 500 crosses everything between, so it has to be drawn while any of
  // that is on screen, even though neither end is.
  //
  // The window is rounded out to whole BLOCKs so this recomputes once every
  // sixty rows of scrolling rather than on every row. Each recompute builds a
  // new edge list and a new bar map, and the overlay rebuilds its paths from
  // them — cheap once, but it was running on every scroll tick, which is what
  // was left of the jitter after the path count came down.
  const DEP_BLOCK = 60;
  const depFrom = Math.floor((first - OVER) / DEP_BLOCK) * DEP_BLOCK;
  const depTo = Math.ceil((last + OVER) / DEP_BLOCK) * DEP_BLOCK;
  const depEdges = useMemo(() => {
    if (!depAll.length) return depAll;
    const from = depFrom;
    const to = depTo;
    return depAll.filter((e) => {
      const a = depRowIndex.get(e.predKey);
      const b = depRowIndex.get(e.succKey);
      if (a === undefined || b === undefined) return false;
      return Math.max(a, b) >= from && Math.min(a, b) <= to;
    });
  }, [depAll, depRowIndex, depFrom, depTo]);

  // Map<issueKey, { left, right, top, bottom, midY, rowTop, rowBottom }> —
  // the shape the overlay reads, for the kept edges' keys only. Keyed off the
  // index map rather than another pass over every row.
  const depBars = useMemo(() => {
    const out = new Map();
    if (!depEdges.length) return out;
    const add = (key) => {
      if (!key || out.has(key)) return;
      const i = depRowIndex.get(key);
      if (i === undefined) return;
      const r = shown[i];
      if (!r) return;
      const bar = clipBar(barOf(r, win, px), chartW);
      if (!bar) return;
      const rowTop = i * rowHeight;
      const midY = rowTop + rowHeight / 2;
      // THE BAR'S OWN TOP AND BOTTOM, not the row's. endpointFor reads both,
      // and an FS path ends `L x ${tgt.top}` — undefined there makes the last
      // segment invalid, SVG keeps the valid prefix, and the arrow runs
      // horizontally to the target's column and stops instead of turning down
      // into it. Same height and centring the bar is drawn with.
      const h = barHeightOf(r);
      out.set(key, {
        left: bar.x,
        right: bar.x + bar.w,
        top: midY - h / 2,
        bottom: midY + h / 2,
        midY,
        rowTop,
        rowBottom: rowTop + rowHeight,
      });
    };
    for (const e of depEdges) { add(e.predKey); add(e.succKey); }
    return out;
  }, [depEdges, depRowIndex, shown, win, px, chartW, rowHeight]);

  // THE OVERLAY IS BUILT ONLY WHEN WHAT IT DRAWS CHANGES. depEdges and depBars
  // move a block of sixty rows at a time; a render in between (a block of
  // rows scrolled, the mouse over a bar) hands React the same element, so it
  // does not walk every arrow again to arrive at the same paths.
  const totalH = shown.length * rowHeight;
  const overlayEl = useMemo(() => (depEdges.length ? (
    <GanttDependencyOverlay
      edges={depEdges}
      bars={depBars}
      phantoms={EMPTY_PHANTOMS}
      width={chartW}
      height={totalH}
    />
  ) : null), [depEdges, depBars, chartW, totalH]);

  // MAKING A LINK BY HAND. Press a dot, drag, let go on another bar's dot.
  // Which kind it is comes from the two ends — finish→start is FS, and so on
  // (deriveType, the folders Gantt's) — and only a kind THIS GRID names a
  // link type for can be made, because that link type is what gets written.
  // The page writes it (onLink); the grid only says which two and which kind.
  depAllRef.current = depAll;
  // The work items already linked to the one the drag started from: their
  // dots stay dark, because a second link between the same two is refused.
  const linkedToFrom = useMemo(() => {
    const out = new Set();
    if (!linkDrag) return out;
    for (const e of depAll) {
      if (e.predKey === linkDrag.key) out.add(e.succKey);
      else if (e.succKey === linkDrag.key) out.add(e.predKey);
    }
    return out;
  }, [linkDrag, depAll]);
  // The line from the dot to the pointer is moved by hand, on the element,
  // not through state: a render per mouse move is a render of the whole grid.
  const linkLayerRef = useRef(null);
  const linkLineRef = useRef(null);
  useEffect(() => {
    if (!linkDrag) return undefined;
    const line = linkLineRef.current;
    if (line) { line.setAttribute('x2', String(linkDrag.x)); line.setAttribute('y2', String(linkDrag.y)); }
    const move = (e) => {
      const layer = linkLayerRef.current;
      const l = linkLineRef.current;
      if (!layer || !l) return;
      const box = layer.getBoundingClientRect();
      l.setAttribute('x2', String(e.clientX - box.left));
      l.setAttribute('y2', String(e.clientY - box.top));
    };
    const stop = () => { linkRef.current = null; setLinkDrag(null); };
    const up = (e) => {
      const from = linkRef.current;
      stop();
      if (!from || !onLink) return;
      // Every element under the pointer, not just the top one: the dot is the
      // one that carries the key, whatever is drawn over it.
      const under = document.elementsFromPoint ? document.elementsFromPoint(e.clientX, e.clientY) : [document.elementFromPoint(e.clientX, e.clientY)];
      const dot = under.find((el) => el && el.getAttribute && el.getAttribute('data-dep-key'));
      if (!dot) return;
      const toKey = dot.getAttribute('data-dep-key');
      const toSide = dot.getAttribute('data-dep-side');
      if (!toKey || toKey === from.key) return;
      const type = deriveType(from.side, toSide);
      const rule = type && depRules ? depRules[type] : null;
      if (!rule || !rule.linkTypeName) return;
      if (pairAlreadyLinked(depAllRef.current, from.key, toKey)) return;
      onLink({ type, predKey: from.key, succKey: toKey });
    };
    const key = (e) => { if (e.key === 'Escape') stop(); };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.removeEventListener('keydown', key);
    };
  }, [linkDrag, depRules, onLink]);

  // The heading row. For a reader it is a row of headings; for an admin in
  // admin mode every heading is the way into that column, and the edge of
  // each one is a resize grip.
  const dirOf = (col) => sortOf(view, col.id);
  const headCell = (col, i) => {
    const last = i === fitted.length - 1;
    // The resize grip works in CONFIG pixels — the width the admin set, not
    // the width it happens to be drawn at once it has been fitted.
    const configWidth = (shownCols.find((c) => c.id === col.id) || col).width;
    const dropSide = colDrop && colDrop.id === col.id ? colDrop.side : null;
    const dropMark = dropSide ? `inset ${dropSide === 'before' ? 3 : -3}px 0 0 0 ${ui.blue}` : null;
    return (
      <div key={col.id} className="wip-head"
        ref={(el) => { headRefs.current[col.id] = el; }}
        title={`${col.label}. Drag the heading to move the column; the arrow sorts by it, the ▾ has its options; drag its edge to resize it.`}
        draggable
        onDragStart={(e) => {
          dragCol.current = col.id;
          setMenuCol(null);
          e.dataTransfer.effectAllowed = 'move';
          try { e.dataTransfer.setData('text/plain', col.label); } catch { /* some browsers refuse */ }
        }}
        onDragOver={(e) => {
          if (!dragCol.current) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (dragCol.current === col.id) { if (colDrop) setColDrop(null); return; }
          const r = e.currentTarget.getBoundingClientRect();
          const side = e.clientX < r.left + r.width / 2 ? 'before' : 'after';
          if (!colDrop || colDrop.id !== col.id || colDrop.side !== side) setColDrop({ id: col.id, side });
        }}
        onDrop={(e) => {
          const moving = dragCol.current;
          if (!moving) return;
          e.preventDefault();
          dragCol.current = null;
          setColDrop(null);
          if (moving === col.id || !dropSide) return;
          const ids = fitted.map((c) => c.id);
          const rest = ids.filter((x) => x !== moving);
          const before = dropSide === 'before' ? col.id : (rest[rest.indexOf(col.id) + 1] || null);
          changeView(withMoved(view, ids, moving, before));
        }}
        onDragEnd={() => { dragCol.current = null; setColDrop(null); }}
        style={{
          ...colStyle(col, last ? null : { borderRight: `1px solid ${ui.border}` }),
          // A HEADING IS READ, NOT TRUNCATED. It wraps to two lines and, if it
          // still does not fit, the type gets smaller until it does — see
          // headingFontSize in ../shared/gridFit. "Estimated Time (Hou…" told
          // a reader nothing, and told them nothing about the column beside it
          // either, which is the whole job of a heading row.
          // Wrapped in full (Page settings), it keeps its full size and takes
          // as many lines as it needs.
          fontSize: wrapFull ? HEAD_MAX : headingFontSize(`${col.agg === 'sum' ? 'Σ ' : ''}${col.label}`, col.width),
          ...(wrapFull ? { padding: '6px 12px' } : null),
          fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
          color: menuCol === col.id ? ui.fg : ui.muted, cursor: 'grab',
          opacity: dragCol.current === col.id ? 0.45 : 1,
          // A pinned heading is sticky (colStyle) and must be opaque, or the
          // headings scrolling under it show through.
          position: frozen && frozen.left[col.id] != null ? 'sticky' : 'relative', userSelect: 'none', whiteSpace: 'normal', lineHeight: 1.15,
          background: menuCol === col.id ? ui.surface : (frozen && frozen.left[col.id] != null ? ui.card : 'transparent'),
          // A COLUMN THIS READER ADDED WEARS THE PRODUCT'S OWN MARK: the
          // accent bar along the top of its heading, the same one Folders
          // draws on a personal column. The dotted underline it had before
          // did not survive the line-clamped heading and read as nothing.
          // Where a dragged heading will land: a blue bar on that side.
          ...(col.mine || dropMark ? { boxShadow: [col.mine ? `inset 0 3px 0 0 ${ui.amber}` : null, dropMark].filter(Boolean).join(', ') } : null),
        }}>
        {/* A PINNED COLUMN SAYS SO on its heading — and an admin may unpin it
            right here; pinning is chosen in Page settings. */}
        {frozen && frozen.left[col.id] != null ? (
          <span role={onFreeze ? 'button' : undefined}
            title={onFreeze ? `${col.label} is pinned at the left — click to unpin it (Page settings chooses which columns pin)` : `${col.label} is pinned at the left — it stays in view while the other columns scroll`}
            onClick={onFreeze ? (e) => { e.stopPropagation(); onFreeze(col.id, false); } : undefined}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ flex: '0 0 auto', display: 'inline-flex', marginRight: 4, cursor: onFreeze ? 'pointer' : 'default', opacity: 0.75 }}>
            <PinIcon size={11} color={ui.muted} />
          </span>
        ) : (frozen && frozen.skipped.has(col.id) ? (
          <span title={`${col.label} is pinned, but the pinned columns would fill this screen — here it scrolls with the rest. Widen the window or pin fewer columns.`}
            style={{ flex: '0 0 auto', display: 'inline-flex', marginRight: 4, opacity: 0.3 }}>
            <PinIcon size={11} color={ui.muted} />
          </span>
        ) : null)}
        <span style={wrapFull ? {
          display: 'block', whiteSpace: 'normal', overflowWrap: 'break-word', width: '100%',
          textAlign: col.align === 'right' ? 'right' : (col.align === 'center' ? 'center' : 'left'),
        } : {
          display: '-webkit-box', WebkitLineClamp: headingLines(`${col.agg === 'sum' ? 'Σ ' : ''}${col.label}`, col.width), WebkitBoxOrient: 'vertical',
          overflow: 'hidden', overflowWrap: 'anywhere', width: '100%',
          textAlign: col.align === 'right' ? 'right' : (col.align === 'center' ? 'center' : 'left'),
        }}>{col.agg === 'sum' ? 'Σ ' : ''}{col.label}</span>
        {/* THE SORT IS AN ARROW, NOT THE HEADING. The heading itself used to
            sort on click, and the mouse-up that ends a resize drag is also a
            click on the heading — so every resize sorted the column. The
            arrow appears on hover, stays while the column is sorted, and
            walks ascending → descending → the structure's own order. */}
        <button className="wip-head-menu"
          onClick={(e) => { e.stopPropagation(); changeView(withSort(view, col.id)); }}
          onMouseDown={(e) => e.stopPropagation()}
          title={dirOf(col) === 'asc' ? `Sorted by ${col.label}, ascending — click for descending`
            : (dirOf(col) === 'desc' ? `Sorted by ${col.label}, descending — click for the structure's own order` : `Sort by ${col.label}`)}
          aria-label={`Sort by ${col.label}`}
          style={{
            flex: '0 0 auto', marginLeft: 3, background: 'none', border: 'none', padding: '0 2px',
            cursor: 'pointer', color: dirOf(col) ? ui.blue : ui.faint, fontSize: 10, lineHeight: 1, fontFamily: FONT,
            opacity: dirOf(col) ? 1 : 0, transition: 'opacity .1s',
          }}>{dirOf(col) === 'desc' ? '▼' : '▲'}</button>
        {/* A READER'S OWN COLUMN CAN BE THROWN AWAY FROM ITS HEADING. The
            way out used to be three deep — the caret, the menu, "Remove this
            column" — and nobody found it. A bin appears on hover beside the
            caret; it stops the click before the heading's sort can hear it,
            so tidying a column never re-orders the tree. */}
        {col.mine ? (
          <button className="wip-head-menu"
            onClick={(e) => { e.stopPropagation(); setMenuCol(null); changeView(withRemoved(view, col.id, { pinnedIds })); }}
            onMouseDown={(e) => e.stopPropagation()}
            title={`Remove ${col.label} — it is your own column, on your screen only`}
            aria-label={`Remove ${col.label}`}
            style={{
              flex: '0 0 auto', marginLeft: 3, background: 'none', border: 'none', padding: '2px 4px',
              cursor: 'pointer', color: ui.red, fontSize: 12, lineHeight: 1, fontFamily: FONT,
              opacity: menuCol === col.id ? 1 : 0, transition: 'opacity .1s',
            }}>🗑</button>
        ) : null}
        {/* The options move to their own button, because the heading itself
            is now the sort — and a control that does two things depending on
            where you hit it is a control nobody trusts. */}
        <button className="wip-head-menu"
          onClick={(e) => { e.stopPropagation(); setMenuCol(menuCol === col.id ? null : col.id); }}
          title={`${col.label} — options`}
          style={{
            flex: '0 0 auto', marginLeft: 2, background: 'none', border: 'none', padding: '0 1px',
            cursor: 'pointer', color: ui.faint, fontSize: 9, lineHeight: 1, fontFamily: FONT,
            opacity: menuCol === col.id ? 1 : 0, transition: 'opacity .1s',
          }}>▾</button>
        <span onMouseDown={(e) => {
            e.preventDefault(); e.stopPropagation();
            // WRITE DOWN WHAT EVERY COLUMN CURRENTLY IS, then drag one.
            // Until a reader touches anything the columns are FITTED to the
            // pane; locking one turns the fitting off for all of them, so
            // without this snapshot the first drag made every other column
            // jump from its fitted width to its configured one.
            const snap = {};
            // The elastic column is written down at its OWN width, not the
            // width it was stretched to — or the slack it was holding would
            // become its width and a column widened afterwards would push the
            // grid off the screen instead of taking that slack back.
            const own = new Map(shownCols.map((c) => [c.id, c.width]));
            fitted.forEach((f) => { snap[f.id] = f.id === fillId && own.has(f.id) ? own.get(f.id) : f.width; });
            setView((v) => withWidths(v, snap));
            // With every width now explicit there is no more scaling, so the
            // mouse and the edge move together, pixel for pixel.
            // With the chart open the DIVIDER travels with the edge (see the
            // drag effect), so it starts from where the columns end now.
            setDrag({ id: col.id, startX: e.clientX, startW: col.width, scale: 1, split: timeline && boxW > 0 ? paneW : null });
          }}
          title="Drag to resize — this width is yours alone"
          style={{ position: 'absolute', right: -3, top: 0, bottom: 0, width: 7, cursor: 'col-resize', zIndex: 2 }} />
      </div>
    );
  };

  const header = (
    <div style={{ display: 'flex', width: paneW, height: '100%', background: ui.card }}>
      {fitted.map(headCell)}
    </div>
  );

  const columnMenu = menuCol ? (() => {
    const mineCol = shownCols.find((x) => x.id === menuCol && x.mine);
    const i = everyColumn.findIndex((x) => x.id === menuCol);
    const col = mineCol || everyColumn[i];
    if (!col) return null;
    return (
      <ColumnMenu col={col} index={i} total={everyColumn.length} cols={everyColumn} fieldList={fieldList}
        ui={ui} S={S} anchorRef={{ current: headRefs.current[menuCol] }}
        // An admin editing the PAGE's column changes it for everyone; every
        // other option here is the reader's own and changes only their view.
        narrow={narrow}
        admin={canEditColumns && !col.mine}
        mine={!!col.mine}
        onChange={(patch) => changeCol(menuCol, patch)}
        onRemove={() => removeCol(menuCol)}
        onMove={(d2) => moveCol(menuCol, d2)}
        pinned={isPinned(col)}
        onHideForMe={isPinned(col) ? null : () => { changeView(withRemoved(view, menuCol, { pinnedIds })); setMenuCol(null); }}
        onPin={canEditColumns && !col.mine ? () => changeCol(menuCol, { pinned: !col.pinned }) : null}
        onClose={() => setMenuCol(null)} />
    );
  })() : null;

  const grainBar = timeline ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '0 2px 6px' }}>
      <span style={S.tiny}>Interval</span>
      {/* DAYS, WEEKS, MONTHS — and not the other two. A bar is a length, and
          at quarters a fortnight of work is four pixels of smudge. Weeks is
          the default; see GANTT_GRAINS in ../shared/interval. */}
      {GANTT_INTERVALS.map((iv) => (
        <button key={iv.id} onClick={() => setGrain(iv.id)} style={{ ...S.pill(useGrain === iv.id), padding: '3px 9px', fontSize: 11.5 }}>{iv.label}</button>
      ))}
      <button onClick={() => toToday()}
        title="Bring today back into view"
        style={{ ...S.exportBtn, padding: '3px 9px', fontSize: 11.5 }}>Today</button>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 12, flexWrap: 'wrap', ...S.tiny }}>
        <span><span style={{ display: 'inline-block', width: 2, height: 11, background: '#f5a623', marginRight: 5, verticalAlign: -2 }} />Today</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: ui.blue, borderRadius: 2, marginRight: 4, verticalAlign: -1 }} />On the plan</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#dc2626', borderRadius: 2, marginRight: 4, verticalAlign: -1 }} />Past its due date</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#4cc38a', borderRadius: 2, marginRight: 4, verticalAlign: -1 }} />Done</span>
        <span>◆ one date only</span>
      </span>
    </div>
  ) : null;

  // A row's background as ONE opaque fill — the pane's layer and the row's
  // own tint over the card — for the pinned cells to paint themselves with.
  const rowFill = (r) => {
    const layer = (c) => `linear-gradient(${c}, ${c})`;
    if (r.kind === 'folder') return ui.surface;
    if (r.kind === 'group') return `${layer(`${ui.surface}88`)}, ${layer(`${ui.surface}dd`)}, ${ui.card}`;
    if (r.kind === 'draft') return `${layer(`${ui.blue}0c`)}, ${ui.card}`;
    return ui.card;
  };

  // ONE scroller, both directions. The header is sticky inside it rather
  // than a second scroller kept in step by hand — two scrollers drift the
  // moment anything else moves one of them (a keyboard scroll, a
  // shift-wheel, the browser restoring a position).
  //
  // The DIVIDER is a sibling of the scroller rather than a cell inside it, and
  // that is the whole trick: the column pane is pinned to the scroller's left
  // edge, so its right-hand edge is always exactly `paneW` from the box's own
  // left edge whatever the chart is doing. An absolute strip at that x follows
  // the pane for free, and never scrolls away from it.
  // ---- the rows ---------------------------------------------------------------
  // ONE ROW, DRAWN. `rowIndex` is its place in `shown` (the timeline cell
  // measures a link's start from it).
  // HOW WIDE A ROW IS DRAWN. With the chart open, the columns and the chart.
  // With it shut, at least the whole grid: columns narrower than the page no
  // longer stretch to fill it (see `fill`), so the row — its line, its hover,
  // its blue when clicked — runs on past the last column to the right-hand
  // edge, as a structure's does, and the grid still reads as full width.
  const rowsW = timeline ? paneW + chartW : Math.max(paneW, boxW);
  const drawRow = (r, rowIndex) => (
    <div key={r.rowId} className={focusAt && focusAt.rowId === r.rowId ? 'wip-row wip-focus' : 'wip-row'}
      // CAPTURE, so the click is noted before any control in the row acts
      // on it — and nothing it does is stopped or changed.
      onMouseDownCapture={(e) => focusFrom(e, r)}
      style={{ position: 'absolute', top: rowIndex * rowHeight, left: 0, width: rowsW, height: rowHeight, borderBottom: `1px solid ${ui.border}`, display: 'flex' }}>
      {/* The columns stay put while the timeline scrolls under
          them — a bar you cannot name is not worth drawing. */}
      <div className="wip-pane" style={{
        position: 'sticky', left: 0, zIndex: 1, flex: `0 0 ${paneW}px`,
        // OPAQUE, whatever the row is. A group's tint carried an
        // alpha and nothing behind it, so the chart layers showed
        // THROUGH the pinned columns — faint marks in cells that
        // hold nothing. The tint is laid over the card instead of
        // replacing it, which keeps the look and stops the bleed.
        // frozenStyle already layers this way; this pane did not.
        background: r.kind === 'folder'
          ? ui.surface
          : (r.kind === 'group'
            ? `linear-gradient(${ui.surface}dd, ${ui.surface}dd), ${ui.card}`
            : ui.card),
        // What a pinned cell paints itself with: this row's look,
        // flattened to an opaque fill (see frozenStyle).
        '--wip-row-bg': rowFill(r),
        borderRight: timeline ? `2px solid ${ui.border}` : 'none',
        // AND ITS OWN ROW LINE. The row's bottom border is the
        // row's, painted beneath the chart's layers — and the
        // pane stopped a pixel short of it, so wherever an arrow
        // ran under the columns it showed through that pixel on
        // every row line: the dark specks in the cells. With the
        // chart on, the pane runs the full row height and draws
        // the line itself, opaque, over the top. Its content box
        // is the same height it always was.
        ...(timeline ? { alignSelf: 'flex-start', height: rowHeight, borderBottom: `1px solid ${ui.border}` } : null),
      }}>
        {renderRow(r)}
      </div>
      {timeline ? timelineCell(r, rowIndex) : null}
    </div>
  );

  return (
    <>
    {columnMenu}
    {grainBar}
    <div style={{ position: 'relative' }}>
    <div ref={scroller} className={`wip-scroll${selected && selected.size ? ' wip-picking' : ''}${linkDrag ? ' wip-linking' : ''}`}
      tabIndex={onSelect ? 0 : undefined}
      /* ONE STATE UPDATE PER FRAME, NOT PER EVENT. A trackpad fires scroll
         events far faster than the browser paints, and every one of them was
         re-rendering thirty rows. Coalescing them onto the next frame is the
         difference between a grid that keeps up and one that stutters — and
         it is where the scroll position is WANTED anyway, since that is when
         it is next drawn. */
      onScroll={onScroll}
      /* THE READER ALWAYS WINS. A wheel, a drag of the scrollbar or a key
         cancels a glide in progress — nobody should have to wait out an
         animation to scroll their own page, and a half-finished glide
         fighting a wheel is how a chart feels broken. */
      onWheel={stopGlide} onMouseDown={stopGlide} onKeyDown={onKeys} onTouchStart={stopGlide}
      style={{
        height: boxH, overflowY: 'auto', overflowX: 'scroll', position: 'relative', scrollbarGutter: 'stable',
        border: `1px solid ${ui.border}`, borderRadius: ui.radiusSm, background: ui.card, outline: 'none',
      }}>
      <style>{`
        .wip-tree-cell:hover .wip-row-tools,
        .wip-tree-cell:hover .wip-plus-wrap { opacity: 1 !important; }
        /* The checkbox: there on hover, there on every row once one is
           ticked, and there on a ticked row whatever the mouse is doing. */
        .wip-pick { opacity: 0; transition: opacity .1s; }
        .wip-row:hover .wip-pick, .wip-picking .wip-pick { opacity: 1; }
        /* Keep it visible while its menu is open, or it vanishes the moment
           the mouse leaves the row to reach the menu. */
        .wip-plus-wrap:focus-within { opacity: 1 !important; }
        .wip-row:hover { background: ${ui.surface}66; }
        /* THE HOVER TINT AND THE CLICKED ROW, OVER THE COLUMNS TOO. The
           pinned pane is opaque (so the chart cannot show through it), which
           also hid the row's own hover tint there: it only ever showed in the
           chart. A veil over the pane carries it — over the pinned cells as
           well — and never takes the pointer. */
        .wip-pane::after { content: ''; position: absolute; inset: 0; z-index: 3; pointer-events: none; }
        /* A whisper of the TEXT colour, not a pale grey: the veil lies over
           the text, and a light wash made the hovered row look disabled. It
           darkens a touch on a light page and lightens on a dark one. */
        .wip-row:hover > .wip-pane::after { background: ${ui.fg}0b; }
        .wip-row.wip-focus, .wip-row.wip-focus > .wip-pane::after { background: ${ui.blue}1c; }
        ${focusAt && focusAt.colId ? `.wip-row.wip-focus [data-col="${String(focusAt.colId).replace(/["\\]/g, '')}"] { box-shadow: inset 0 0 0 2px ${ui.blue}; }` : ''}
        .wip-rule:hover { background: ${ui.amber}1f; }
        .wip-rule:hover .wip-rule-pen { opacity: 0.9 !important; }
        /* The horizontal scrollbar is how a reader reaches the columns off
           the right-hand edge, so it must be VISIBLE and it must sit at the
           bottom of the grid — not at the bottom of the page, and not
           hidden until the mouse moves (which is what an overlay scrollbar
           does, and why a whole column can go unnoticed). */
        .wip-scroll { scrollbar-width: auto; scrollbar-color: ${ui.muted} transparent; }
        .wip-scroll::-webkit-scrollbar { width: 12px; height: 13px; }
        .wip-scroll::-webkit-scrollbar-track { background: ${ui.surface}; }
        .wip-scroll::-webkit-scrollbar-thumb { background: ${ui.border}; border-radius: 7px; border: 3px solid ${ui.surface}; }
        .wip-scroll::-webkit-scrollbar-thumb:hover { background: ${ui.muted}; }
        .wip-scroll::-webkit-scrollbar-corner { background: ${ui.surface}; }
        /* NO SPINNERS. A number cell is 80 pixels wide; a pair of stepper
           arrows takes a fifth of that, covers the digits they are meant to
           help with, and nudge by one — which is never the edit anybody came
           to make in an hours or a rand column. The keyboard still steps with
           the arrow keys for anyone who wants that. */
        .wip-num { -moz-appearance: textfield; appearance: textfield; }
        .wip-num::-webkit-outer-spin-button,
        .wip-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        /* The twisty is the most-clicked thing on the page; it gets a plate
           under the mouse so it reads as a button and not as a character. */
        .wip-twisty:hover { background: ${ui.surface} !important; color: ${ui.blue} !important; }
        .wip-head:hover .wip-head-menu { opacity: 1 !important; }
        .wip-split:hover span, .wip-split-on span { background: ${ui.primary} !important; opacity: 1 !important; }
        /* THE ARROWS ARE A DRAWING HERE, NOT A CONTROL — the grid offers
           nothing to do with a clicked arrow — so they must not take the
           pointer from what is under them: an FS arrow ends exactly where
           its target bar's start dot sits. */
        .wip-scroll .gantt-dep-arrow { pointer-events: none; }
        /* AND NO SHADOW UNDER THEM. The shared overlay gives every arrow a
           drop-shadow filter, and a filter is rendered over the line's whole
           bounding box — for an arrow from a milestone to a task four hundred
           rows down, a box the height of the plan. The GPU re-ran it for
           every tile that scrolled in: a real trace of an expanded plan spent
           ten seconds in GPU work and four hundred milliseconds a frame in
           Commit, and with the filter off the same scroll went from 740 ms a
           frame to 17. The shadow was a 15% tint; the line reads the same. */
        .wip-scroll .gantt-dep-arrow-line { filter: none; }
        /* The link dots: there while the pointer is on the bar, and while a
           link is being drawn, there (and a little larger, to be easy to hit)
           on every bar it could finish on. */
        .wip-dep-dot { opacity: 0; transition: opacity .1s, transform .1s; }
        .wip-bar:hover .wip-dep-dot, .wip-dep-dot.wip-dep-on { opacity: 1; }
        .wip-dep-dot:hover, .wip-dep-dot.wip-dep-on { transform: scale(1.3); }
        .wip-linking, .wip-linking * { cursor: crosshair !important; }
        ${touchCss}
      `}</style>
      <div style={{ width: paneW + chartW, minWidth: '100%' }}>
        <div ref={headRowRef} style={{
          position: 'sticky', top: 0, zIndex: 3, background: ui.card,
          ...(wrapFull ? { minHeight: 38 } : { height: 38 }),
          borderBottom: `2px solid ${ui.border}`, display: 'flex',
        }}>
          <div style={{ position: 'sticky', left: 0, zIndex: 1, flex: `0 0 ${paneW}px`, background: ui.card, borderRight: timeline ? `2px solid ${ui.border}` : 'none', display: 'flex' }}>
            {header}
          </div>
          {!timeline ? (
            <AddColumn cols={shownCols} hidden={canEditColumns ? hiddenColumns : []} fieldList={fieldList} ui={ui} S={S}
              // ANY READER MAY ADD A COLUMN — to their own view, from the
              // fields this site already has. An admin can also add one for
              // everybody, which is the other half of the same menu.
              canShare={canEditColumns}
              mineCount={view.added.length}
              hiddenForMe={view.hidden}
              onAddMine={(fld) => changeView(withAdded(view, columnFor(fld)))}
              onAdd={canEditColumns ? (fld) => onColumns((list) => [...list, columnFor(fld)]) : null}
              onShow={canEditColumns ? (id) => changeCol(id, { on: true }) : null}
              onShowForMe={(id) => changeView({ ...view, hidden: view.hidden.filter((h) => h !== id) })}
              onReset={isEmptyView(view) ? null : () => changeView(resetView())}
              narrow={narrow} />
          ) : null}
          {timeline ? tickHeader : null}
        </div>
        {shown.length === 0
          ? <div style={{ ...S.tiny, padding: '24px 12px', textAlign: 'center' }}>{emptyText}</div>
          : (
            <div style={{ height: shown.length * rowHeight, position: 'relative' }}>
              {chartBackdrop}
              {/* EACH ROW AT ITS OWN PLACE, not stacked in a box that moves.
                  The box used to sit at the window's first row with the rows
                  in flow inside it, so every block of scrolling moved the box
                  AND re-flowed every row in it — the browser laid out and
                  repainted the whole window to show twelve new rows. Placed
                  absolutely, a row that stays in the window does not move at
                  all; only the rows coming and going are touched. */}
              <div style={{ position: 'absolute', top: 0, left: 0, width: rowsW }}>
                {slice.map((r, i) => drawRow(r, first + i))}
              </div>
              {/* ARROWS ABOVE THE BARS, UNDER THE PINNED COLUMNS.
                  Both halves of that are stacking order, and both bite.
                  It is drawn AFTER the slice so it paints over the bars
                  (same level, later wins), and at z-index 0 so the row's
                  sticky label pane at z-index 1 and the frozen cells at 2
                  paint over IT — an arrow reaching back to an earlier date
                  scrolls under the columns, where at z-index 2 it was drawn
                  straight across the Assigned Resource and Value cells.
                  The slice sets no z-index of its own, so the panes inside
                  it stack against this one rather than against the slice. */}
              {timeline && (depEdges.length > 0 || linkDrag) && (
                <div style={{ position: 'absolute', left: paneW, top: backdropTop, width: chartW,
                              height: backdropH, pointerEvents: 'none', zIndex: 0,
                              // CLIPPED TO THE CHART. The overlay's svg sets
                              // overflow:visible so an arrowhead is never cut
                              // at its own edge, which also lets a stray
                              // segment paint outside the chart box — over the
                              // columns, where it reads as marks in the cells.
                              // The box is the chart; nothing of it belongs
                              // anywhere else.
                              //
                              // AND TO THE ROWS THAT ARE DRAWN, like the
                              // backdrop. Where the rows have not been drawn
                              // yet (a fast scroll outrunning them) there is
                              // nothing over the chart at all, columns
                              // included — and an arrow drawn there ran
                              // straight across the empty page.
                              overflow: 'hidden' }}>
                  {/* The drawing keeps the whole chart's coordinates: it is
                      shifted up by the clip's own offset, so a path is
                      exactly where it always was. */}
                  <div ref={linkLayerRef} style={{ position: 'absolute', left: 0, top: -backdropTop, width: chartW, height: totalH }}>
                    {overlayEl}
                    {/* The link being drawn: from the dot pressed to the
                        pointer, moved by the drag's own listener. */}
                    {linkDrag ? (
                      <svg width={1} height={1} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' }}>
                        <line ref={linkLineRef} x1={linkDrag.x} y1={linkDrag.y} stroke={ui.primary || ui.blue} strokeWidth={1.5} strokeDasharray="5 4" />
                      </svg>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          )}
      </div>
    </div>
    {/* HOW MUCH IS COLUMNS AND HOW MUCH IS CHART IS THE READER'S CALL. One
        person is chasing dates and wants the bars; the next is reconciling
        hours and wants the figures. The line between them is remembered as a
        fraction of the grid, and it cannot be dragged so far that either side
        stops being usable — see clampSplit in ../shared/gridFit. It stops
        short of the bottom so the horizontal scrollbar stays reachable. */}
    {timeline && boxW > 0 ? (
      <div className={splitDrag ? 'wip-split-on' : 'wip-split'}
        onMouseDown={(e) => { e.preventDefault(); setSplitDrag({ x0: e.clientX, w0: paneW }); }}
        onDoubleClick={() => rememberSplit(0)}
        title="Drag to share the width between the columns and the timeline — double-click to reset"
        style={{
          position: 'absolute', top: 1, bottom: 15, left: paneW - 4, width: 11, zIndex: 6,
          cursor: 'col-resize', display: 'flex', justifyContent: 'center',
        }}>
        <span style={{ width: 3, borderRadius: 3, background: ui.border, opacity: 0.55 }} />
      </div>
    ) : null}
    </div>
    </>
  );
}
